import type {
  WidgetConfig,
  WidgetState,
  City,
  DeliveryOption,
  PickupPoint,
  CartData,
  DeliveryCategory,
  CourierAddress,
  CategoryConfig,
  DeliveryOrderPayload,
} from '../types';
import type { PvzProviderFilter, PvzTypeFilter, TariffProviderFilter } from '../types';
import { LS_ORDER_BUFFER_KEY } from '../types';
import { PROVIDER_LOGOS, MOSCOW_CDEK_ID } from '../types';
import { ApiClient } from '../api/client';
import { injectStyles } from './styles';

export class DeliveryWidget {
  private container: HTMLElement;
  private config: WidgetConfig;
  private api: ApiClient;
  private state: WidgetState;
  private debounceTimer: number | null = null;
  private pickupPoints: PickupPoint[] = [];
  private filteredPoints: PickupPoint[] = [];
  private mapInstance: any = null;
  private clusterer: any = null;
  private pvzProviderFilter: PvzProviderFilter = 'all';
  private pvzTypeFilter: PvzTypeFilter = 'all';
  private pvzSearchQuery: string = '';
  private mapSelectedPoint: PickupPoint | null = null;
  private tariffProviderFilter: TariffProviderFilter = 'all';
  private bottomSheetState: 'collapsed' | 'expanded' | 'pvz-selected' = 'collapsed';
  private isMobile: boolean = false;

  constructor(container: HTMLElement, config: WidgetConfig) {
    this.container = container;
    this.config = config;
    this.api = new ApiClient(config.apiUrl);
    this.state = {
      city: null,
      options: [],
      selectedOption: null,
      selectedPickupPoint: null,
      isLoading: false,
      error: null,
      showPickupMap: false,
      activeCategory: null,
      courierAddress: null,
    };
  }

  /**
   * Initialize widget
   */
  async init(cartData: CartData): Promise<void> {
    injectStyles();
    this.render();
    this.attachEventListeners();

    // Clear stale delivery rows from Tilda sidebar before restoring selection
    this.hideStaleDeliveryRows();

    // Restore saved city
    const savedCity = localStorage.getItem('gw_delivery_city');
    if (savedCity) {
      try {
        this.state.city = JSON.parse(savedCity);
        this.updateCityInput();
        await this.calculateDelivery(cartData);
      } catch {
        localStorage.removeItem('gw_delivery_city');
      }
    }
  }

  /**
   * Render widget HTML
   */
  private render(): void {
    this.container.innerHTML = `
      <div class="gw-delivery">
        <div class="gw-delivery__header">
          <h3 class="gw-delivery__title">Способ доставки</h3>
        </div>

        <div class="gw-delivery__city">
          <label class="gw-delivery__label">Город доставки</label>
          <div class="gw-delivery__city-wrapper">
            <input
              type="text"
              class="gw-delivery__city-input"
              placeholder="Начните вводить название города..."
              autocomplete="off"
            />
            <div class="gw-delivery__suggestions"></div>
          </div>
        </div>

        <!-- Category Badges -->
        <div class="gw-delivery__badges-container">
          ${this.renderCategoryBadges()}
        </div>

        <!-- Tariff Cards for selected category -->
        <div class="gw-delivery__tariffs-container">
          ${this.renderTariffCards()}
        </div>

        <!-- Address form for courier delivery -->
        <div class="gw-delivery__address-container" style="display: ${this.state.activeCategory === 'courier' ? 'block' : 'none'};">
          ${this.renderAddressForm()}
        </div>

        <!-- Legacy options container (hidden, kept for compatibility) -->
        <div class="gw-delivery__options-container" style="display: none;">
          ${this.renderOptions()}
        </div>

        <div class="gw-delivery__message-container"></div>
      </div>
    `;
  }

  /**
   * Get category configurations based on available options
   */
  private getCategoryConfigs(): CategoryConfig[] {
    const isMoscow = this.state.city?.cdek_city_id === MOSCOW_CDEK_ID;
    const hasPickupOptions = this.state.options.some(o => o.type === 'pickup');
    const hasCourierOptions = this.state.options.some(o => o.type === 'courier');

    return [
      {
        id: 'greenwax',
        label: 'Greenwax',
        subtitle: 'Бесплатно',
        icon: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>',
        enabled: isMoscow,
        disabled: !isMoscow,
      },
      {
        id: 'courier',
        label: 'Курьер',
        subtitle: 'До двери',
        icon: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>',
        enabled: hasCourierOptions || this.state.isLoading,
      },
      {
        id: 'pickup',
        label: 'ПВЗ',
        subtitle: 'Самовывоз',
        icon: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>',
        enabled: hasPickupOptions || this.state.isLoading,
      },
      {
        id: 'post',
        label: 'Почта',
        subtitle: 'Скоро',
        icon: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>',
        enabled: false,
        disabled: true,
      },
    ];
  }

  /**
   * Render category badges
   */
  private renderCategoryBadges(): string {
    if (!this.state.city) {
      return '<div class="gw-delivery__no-options"><div class="gw-delivery__no-options-text">Выберите город для расчёта доставки</div></div>';
    }

    // Don't show badges until options are loaded (after calculation)
    if (this.state.options.length === 0 && !this.state.isLoading) {
      return '';
    }

    const categories = this.getCategoryConfigs();

    return `
      <div class="gw-delivery__badges">
        ${categories.map(cat => `
          <button
            type="button"
            class="gw-delivery__badge ${this.state.activeCategory === cat.id ? 'gw-delivery__badge--active' : ''} ${cat.disabled ? 'gw-delivery__badge--disabled' : ''}"
            data-category="${cat.id}"
            ${cat.disabled ? 'disabled title="Скоро"' : ''}
          >
            <span class="gw-delivery__badge-icon">${cat.icon}</span>
            <span class="gw-delivery__badge-label">${cat.label}</span>
            <span class="gw-delivery__badge-subtitle">${cat.subtitle}</span>
            ${this.getBadgeCount(cat.id) > 0 ? '<span class="gw-delivery__badge-count">' + this.getBadgeCount(cat.id) + '</span>' : ''}
          </button>
        `).join('')}
      </div>
    `;
  }

  /**
   * Get unique providers available in the current category
   */
  private getProvidersForCategory(categoryType: 'courier' | 'pickup'): string[] {
    const providers = new Set<string>();
    this.state.options
      .filter(o => o.type === categoryType)
      .forEach(o => providers.add(o.provider));
    return Array.from(providers);
  }

  /**
   * Render provider filter pills for the active category
   */
  private renderProviderFilters(): string {
    if (!this.state.activeCategory || this.state.activeCategory === 'greenwax' || this.state.activeCategory === 'post') {
      return '';
    }

    const categoryType = this.state.activeCategory === 'courier' ? 'courier' : 'pickup';
    const providers = this.getProvidersForCategory(categoryType as 'courier' | 'pickup');

    // Don't show filter if only one provider
    if (providers.length <= 1) {
      return '';
    }

    const providerLabels: Record<string, string> = {
      cdek: 'СДЭК',
      yandex: 'Яндекс',
    };

    const filters = [
      { id: 'all' as TariffProviderFilter, label: 'Все' },
      ...providers.map(p => ({
        id: p as TariffProviderFilter,
        label: providerLabels[p] || p,
      })),
    ];

    return `
      <div class="gw-delivery__provider-filters">
        ${filters.map(f => `
          <button
            type="button"
            class="gw-delivery__provider-filter ${this.tariffProviderFilter === f.id ? 'gw-delivery__provider-filter--active' : ''}"
            data-provider-filter="${f.id}"
          >${f.label}</button>
        `).join('')}
      </div>
    `;
  }

  /**
   * Get courier options collapsed to cheapest + fastest per provider.
   */
  private getCourierOptionsForDisplay(): DeliveryOption[] {
    const courierOptions = this.state.options.filter(o => o.type === 'courier');

    // Group by provider
    const byProvider = new Map<string, DeliveryOption[]>();
    for (const opt of courierOptions) {
      if (!byProvider.has(opt.provider)) byProvider.set(opt.provider, []);
      byProvider.get(opt.provider)!.push(opt);
    }

    const result: DeliveryOption[] = [];
    for (const [, providerOpts] of byProvider) {
      if (providerOpts.length <= 1) {
        result.push(...providerOpts);
        continue;
      }
      // Cheapest
      const cheapest = providerOpts.reduce((a, b) => a.price < b.price ? a : b);
      // Fastest
      const fastest = providerOpts.reduce((a, b) => {
        if (a.max_days !== b.max_days) return a.max_days < b.max_days ? a : b;
        if (a.min_days !== b.min_days) return a.min_days < b.min_days ? a : b;
        return a.price < b.price ? a : b;
      });
      result.push(cheapest);
      if (cheapest.id !== fastest.id) {
        result.push(fastest);
      }
    }
    return result;
  }

  /**
   * Compute cheapest/fastest badges for tariff cards.
   * For each provider with 2+ options of the same type, labels them.
   */
  private computeTariffBadges(options: DeliveryOption[]): Map<string, 'cheapest' | 'fastest'> {
    const badges = new Map<string, 'cheapest' | 'fastest'>();

    // Group by provider
    const byProvider = new Map<string, DeliveryOption[]>();
    for (const opt of options) {
      const key = opt.provider;
      if (!byProvider.has(key)) byProvider.set(key, []);
      byProvider.get(key)!.push(opt);
    }

    for (const [, providerOpts] of byProvider) {
      if (providerOpts.length < 2) continue;

      // Find cheapest and fastest
      const cheapest = providerOpts.reduce((a, b) => a.price < b.price ? a : b);
      const fastest = providerOpts.reduce((a, b) => {
        if (a.max_days !== b.max_days) return a.max_days < b.max_days ? a : b;
        if (a.min_days !== b.min_days) return a.min_days < b.min_days ? a : b;
        return a.price < b.price ? a : b;
      });

      if (cheapest.id !== fastest.id) {
        badges.set(cheapest.id, 'cheapest');
        badges.set(fastest.id, 'fastest');
      }
    }

    return badges;
  }

  /**
   * Render tariff cards for selected category
   */
  private renderTariffCards(): string {
    if (!this.state.activeCategory) {
      if (this.state.error) {
        return `
          <div class="gw-delivery__no-options">
            <div class="gw-delivery__no-options-icon"><svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg></div>
            <div class="gw-delivery__no-options-text">${this.state.error}</div>
          </div>
        `;
      }
      return '';
    }

    if (this.state.isLoading) {
      return this.renderSkeletonCards();
    }

    // Handle Greenwax self-pickup
    if (this.state.activeCategory === 'greenwax') {
      return this.renderGreenwaxPickup();
    }

    // Handle ПВЗ — show pickup summary (map-first flow)
    if (this.state.activeCategory === 'pickup') {
      return this.renderPickupSummary();
    }

    // Courier: show collapsed options (cheapest + fastest per provider)
    let filteredOptions = this.getCourierOptionsForDisplay();

    if (filteredOptions.length === 0) {
      return `
        <div class="gw-delivery__no-options">
          <div class="gw-delivery__no-options-icon"><svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 002 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg></div>
          <div class="gw-delivery__no-options-text">Курьерская доставка недоступна для этого города</div>
        </div>
      `;
    }

    // Apply provider filter for courier
    if (this.tariffProviderFilter !== 'all') {
      filteredOptions = filteredOptions.filter(o => o.provider === this.tariffProviderFilter);
    }

    // Compute cheapest/fastest badges per provider
    const badges = this.computeTariffBadges(filteredOptions);

    return `
      <div class="gw-delivery__tariffs">
        ${filteredOptions.map(option => this.renderTariffCard(option, badges.get(option.id))).join('')}
      </div>
    `;
  }

  /**
   * Render single tariff card
   */
  private renderTariffCard(option: DeliveryOption, badge?: 'cheapest' | 'fastest'): string {
    const isSelected = this.state.selectedOption?.id === option.id;
    const logoUrl = PROVIDER_LOGOS[option.provider] || PROVIDER_LOGOS.cdek;
    const daysText = this.formatDeliveryDays(option.min_days, option.max_days);
    const priceText = option.is_free ? 'Бесплатно' : `${option.price.toLocaleString('ru-RU')} ₽`;

    // Format pickup points count for display
    const pickupCountText = option.type === 'pickup' && option.pickup_points_count
      ? ` • ${option.pickup_points_count} ${this.pluralizePoints(option.pickup_points_count)}`
      : '';

    // Cheapest / Fastest badge
    const tariffBadge = badge === 'cheapest'
      ? '<span class="gw-delivery__tariff-badge gw-delivery__tariff-badge--cheapest">Выгодно</span>'
      : badge === 'fastest'
        ? '<span class="gw-delivery__tariff-badge gw-delivery__tariff-badge--fastest">Быстрее</span>'
        : '';

    // Free delivery badge
    const freeBadge = option.is_free
      ? "<span class=\"gw-delivery__free-badge\"><svg viewBox=\"0 0 16 16\" width=\"12\" height=\"12\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2\"><polyline points=\"13 3 6 12 3 9\"/></svg> Бесплатно</span>"
      : "";

    const selectButton = isSelected
      ? '<button type="button" class="gw-delivery__tariff-select gw-delivery__tariff-select--selected">Выбрано</button>'
      : '';

    // Show "Выбрать ПВЗ" button for pickup options when selected
    const pvzButton = option.type === 'pickup' && isSelected && !this.state.selectedPickupPoint
      ? `<button type="button" class="gw-delivery__pvz-select-btn" data-option-id="${option.id}">Выбрать ПВЗ на карте</button>`
      : '';

    // Show selected pickup point info
    const selectedPvzInfo = option.type === 'pickup' && isSelected && this.state.selectedPickupPoint
      ? `<div class="gw-delivery__selected-pvz-info">
          <div class="gw-delivery__selected-pvz-name">${this.state.selectedPickupPoint.name}</div>
          <div class="gw-delivery__selected-pvz-address">${this.state.selectedPickupPoint.address}</div>
          <button type="button" class="gw-delivery__pvz-change-btn" data-option-id="${option.id}">Изменить ПВЗ</button>
        </div>`
      : '';

    return `
      <div class="gw-delivery__tariff-card ${isSelected ? 'gw-delivery__tariff-card--selected' : ''}"
           data-option-id="${option.id}">
        ${isSelected ? '<div class=\"gw-delivery__tariff-check\"><svg viewBox=\"0 0 24 24\"><polyline points=\"20 6 9 17 4 12\"/></svg></div>' : ''}
        <div class="gw-delivery__tariff-info">
          <img src="${logoUrl}" alt="${option.provider}" class="gw-delivery__tariff-logo" onerror="this.style.display='none'" />
          <div class="gw-delivery__tariff-details">
            <div class="gw-delivery__tariff-name">${option.name} ${tariffBadge}</div>
            <div class="gw-delivery__tariff-days">${daysText}${pickupCountText}</div>
          </div>
        </div>
        <div class="gw-delivery__tariff-pricing">
          <span class="gw-delivery__tariff-price ${option.is_free ? 'gw-delivery__tariff-price--free' : ''}">${priceText}</span>
          ${freeBadge}
          ${selectButton}
        </div>
        ${pvzButton}
        ${selectedPvzInfo}
      </div>
    `;
  }

  /**
   * Pluralize "пунктов" in Russian
   */
  private pluralizePoints(n: number): string {
    const lastDigit = n % 10;
    const lastTwoDigits = n % 100;
    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return 'пунктов';
    if (lastDigit === 1) return 'пункт';
    if (lastDigit >= 2 && lastDigit <= 4) return 'пункта';
    return 'пунктов';
  }

  /**
   * Format delivery days text
   */
  private formatDeliveryDays(minDays: number, maxDays: number): string {
    if (minDays === maxDays) {
      return `${minDays} ${this.pluralizeDays(minDays)}`;
    }
    return `${minDays}-${maxDays} ${this.pluralizeDays(maxDays)}`;
  }

  /**
   * Pluralize days in Russian
   */
  private pluralizeDays(n: number): string {
    const lastDigit = n % 10;
    const lastTwoDigits = n % 100;
    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return 'дней';
    if (lastDigit === 1) return 'день';
    if (lastDigit >= 2 && lastDigit <= 4) return 'дня';
    return 'дней';
  }

  /**
   * Get count for category badge.
   * ПВЗ: total pickup points count (from all providers).
   * Курьер: number of unique courier providers.
   */
  private getBadgeCount(categoryId: string): number {
    if (categoryId === 'greenwax') return 0;
    if (categoryId === 'post') return 0;

    if (categoryId === 'pickup') {
      // Sum pickup_points_count across all pickup options (unique per provider)
      const seen = new Set<string>();
      let total = 0;
      for (const o of this.state.options) {
        if (o.type === 'pickup' && !seen.has(o.provider)) {
          seen.add(o.provider);
          total += o.pickup_points_count || 0;
        }
      }
      return total;
    }

    if (categoryId === 'courier') {
      const providers = new Set<string>();
      for (const o of this.state.options) {
        if (o.type === 'courier') providers.add(o.provider);
      }
      return providers.size;
    }

    return 0;
  }

  /**
   * Render skeleton loading cards
   */
  private renderSkeletonCards(): string {
    const cards = [0, 1, 2].map((i) => {
      const delay = i * 80;
      return '<div class="gw-delivery__skeleton-card" style="animation-delay: ' + delay + 'ms">' +
        '<div class="gw-delivery__skeleton-logo"></div>' +
        '<div class="gw-delivery__skeleton-lines">' +
          '<div class="gw-delivery__skeleton-line"></div>' +
          '<div class="gw-delivery__skeleton-line gw-delivery__skeleton-line--short"></div>' +
        '</div>' +
        '<div class="gw-delivery__skeleton-price"></div>' +
      '</div>';
    });
    return '<div class="gw-delivery__tariffs">' + cards.join('') + '</div>';
  }

  /**
   * Render pickup summary card (after PVZ selected on map)
   */
  private renderPickupSummary(): string {
    const point = this.state.selectedPickupPoint;
    const option = this.state.selectedOption;

    // If no PVZ selected yet, show prompt to open map
    if (!point || !option) {
      const hasPickupOptions = this.state.options.some(o => o.type === 'pickup');
      if (!hasPickupOptions) {
        return `
          <div class="gw-delivery__no-options">
            <div class="gw-delivery__no-options-icon"><svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg></div>
            <div class="gw-delivery__no-options-text">Нет доступных пунктов выдачи</div>
          </div>
        `;
      }
      return `
        <div class="gw-delivery__pickup-prompt">
          <div class="gw-delivery__pickup-prompt-icon"><svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg></div>
          <div class="gw-delivery__pickup-prompt-text">Выберите пункт выдачи на карте</div>
          <button type="button" class="gw-delivery__pickup-prompt-btn" data-action="open-pvz-map">Открыть карту</button>
        </div>
      `;
    }

    // PVZ is selected — show summary
    const providerLabel = point.provider === 'yandex' ? 'Яндекс' : 'СДЭК';
    const logoUrl = PROVIDER_LOGOS[point.provider as keyof typeof PROVIDER_LOGOS] || PROVIDER_LOGOS.cdek;
    const daysText = this.formatDeliveryDays(option.min_days, option.max_days);
    const priceText = option.is_free ? 'Бесплатно' : `${option.price.toLocaleString('ru-RU')} ₽`;
    const typeLabel = point.type === 'postamat' ? 'Постамат' : 'ПВЗ';

    return `
      <div class="gw-delivery__pickup-summary">
        <div class="gw-delivery__pickup-summary-header">
          <img src="${logoUrl}" alt="${option.provider}" class="gw-delivery__tariff-logo" onerror="this.style.display='none'" />
          <div class="gw-delivery__pickup-summary-info">
            <div class="gw-delivery__pickup-summary-provider">
              <span class="gw-delivery__pickup-summary-badge gw-delivery__pickup-summary-badge--${point.provider}">${providerLabel}</span>
              <span class="gw-delivery__pickup-summary-badge gw-delivery__pickup-summary-badge--type">${typeLabel}</span>
            </div>
            <div class="gw-delivery__pickup-summary-name">${point.name}</div>
            <div class="gw-delivery__pickup-summary-address">${point.address}</div>
            ${point.work_time ? '<div class="gw-delivery__pickup-summary-hours"><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> ' + point.work_time + '</div>' : ''}
          </div>
          <div class="gw-delivery__pickup-summary-price">
            <span class="gw-delivery__tariff-price ${option.is_free ? 'gw-delivery__tariff-price--free' : ''}">${priceText}</span>
            <span class="gw-delivery__pickup-summary-days">${daysText}</span>
          </div>
        </div>
        <button type="button" class="gw-delivery__pickup-change-btn" data-action="open-pvz-map">Изменить пункт выдачи</button>
      </div>
    `;
  }

  /**
   * Render Greenwax self-pickup info
   */
  private renderGreenwaxPickup(): string {
    return `
      <div class="gw-delivery__greenwax-info">
        <h4 class="gw-delivery__greenwax-title"><svg class="gw-delivery__greenwax-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/><polyline points="9 22 9 12 15 12 15 22"/></svg> Самовывоз из офиса Greenwax</h4>
        <div class="gw-delivery__greenwax-address">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg> Москва, Боровское шоссе, д. 2, корп. 6
        </div>
        <div class="gw-delivery__greenwax-worktime">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> Пн-Пт: 10:00-19:00, Сб: 11:00-17:00
        </div>
        <div class="gw-delivery__greenwax-price">Бесплатно${this.state.selectedOption?.id === 'greenwax_pickup' ? ' <span style="color:#27ae60;">✓ Выбрано</span>' : ''}</div>
      </div>
    `;
  }

  /**
   * Render courier address form
   */
  private renderAddressForm(): string {
    const address = this.state.courierAddress || { fullName: '', street: '', house: '', apartment: '', comment: '' };

    return `
      <div class="gw-delivery__address">
        <h4 class="gw-delivery__address-title">Адрес доставки</h4>

        <div class="gw-delivery__address-field">
          <label>Получатель (ФИО полностью)</label>
          <input
            type="text"
            name="fullName"
            class="gw-delivery__address-input"
            placeholder="Иванов Иван Иванович"
            value="${address.fullName || ''}"
          />
        </div>

        <div class="gw-delivery__address-field">
          <label>Улица</label>
          <div class="gw-delivery__address-autocomplete">
            <input
              type="text"
              name="street"
              class="gw-delivery__address-input gw-delivery__street-input"
              placeholder="Начните вводить название улицы..."
              value="${address.street || ''}"
              autocomplete="off"
            />
            <div class="gw-delivery__street-suggestions"></div>
          </div>
        </div>

        <div class="gw-delivery__address-row">
          <div class="gw-delivery__address-field gw-delivery__address-field--third">
            <label>Дом</label>
            <input
              type="text"
              name="house"
              class="gw-delivery__address-input"
              placeholder="10А"
              value="${address.house || ''}"
            />
          </div>
          <div class="gw-delivery__address-field gw-delivery__address-field--third">
            <label>Квартира/офис</label>
            <input
              type="text"
              name="apartment"
              class="gw-delivery__address-input"
              placeholder="25"
              value="${address.apartment || ''}"
            />
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Render delivery options
   */
  private renderOptions(): string {
    if (this.state.isLoading) {
      return `
        <div class="gw-delivery__loading">
          <div class="gw-delivery__spinner"></div>
          <span>Рассчитываем стоимость доставки...</span>
        </div>
      `;
    }

    if (this.state.error) {
      return `<div class="gw-delivery__error"><svg class="gw-delivery__error-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg><div class="gw-delivery__error-text">${this.state.error}</div></div>`;
    }

    if (!this.state.city) {
      return `<div class="gw-delivery__empty">Укажите город для расчета доставки</div>`;
    }

    if (this.state.options.length === 0) {
      return `<div class="gw-delivery__empty">Нет доступных способов доставки</div>`;
    }

    // Group options by type
    const pickupOptions = this.state.options.filter(o => o.type === 'pickup');
    const courierOptions = this.state.options.filter(o => o.type === 'courier');

    return `
      <div class="gw-delivery__options">
        ${pickupOptions.length > 0 ? `
          <div class="gw-delivery__options-group">
            <div class="gw-delivery__options-group-title">
              <svg class="gw-delivery__options-group-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
              Самовывоз из пункта выдачи
            </div>
            ${pickupOptions.map(option => this.renderOption(option)).join('')}
          </div>
        ` : ''}
        ${courierOptions.length > 0 ? `
          <div class="gw-delivery__options-group">
            <div class="gw-delivery__options-group-title">
              <svg class="gw-delivery__options-group-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="1" y="3" width="15" height="13"></rect>
                <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
                <circle cx="5.5" cy="18.5" r="2.5"></circle>
                <circle cx="18.5" cy="18.5" r="2.5"></circle>
              </svg>
              Курьерская доставка
            </div>
            ${courierOptions.map(option => this.renderOption(option)).join('')}
          </div>
        ` : ''}
      </div>
    `;
  }

  /**
   * Render single option
   */
  private renderOption(option: DeliveryOption): string {
    const isSelected = this.state.selectedOption?.id === option.id;
    const priceText = option.is_free
      ? 'Бесплатно'
      : `${option.price.toLocaleString('ru-RU')} ₽`;

    let additionalContent = '';

    if (option.type === 'pickup') {
      // Pickup point selection
      if (this.state.selectedPickupPoint && isSelected) {
        additionalContent = `
          <div class="gw-delivery__selected-pvz">
            <div class="gw-delivery__selected-pvz-name">${this.state.selectedPickupPoint.name}</div>
            <div class="gw-delivery__selected-pvz-address">${this.state.selectedPickupPoint.address}</div>
            <span class="gw-delivery__selected-pvz-change" data-action="change-pvz">Изменить ПВЗ</span>
          </div>
        `;
      } else {
        additionalContent = `
          <button type="button" class="gw-delivery__pvz-btn" data-option-id="${option.id}">
            Выбрать пункт выдачи
          </button>
        `;
      }
    } else if (option.type === 'courier') {
      // Courier delivery badge
      additionalContent = `
        <div class="gw-delivery__courier-info">
          <span class="gw-delivery__courier-badge">Доставка до двери</span>
        </div>
      `;
    }

    // Format delivery time
    const daysText = option.min_days === option.max_days
      ? `${option.min_days} ${this.pluralizeDays(option.min_days)}`
      : `${option.min_days}–${option.max_days} дней`;

    return `
      <label class="gw-delivery__option ${isSelected ? 'gw-delivery__option--selected' : ''}">
        <input
          type="radio"
          name="gw_delivery_option"
          value="${option.id}"
          ${isSelected ? 'checked' : ''}
        />
        <div class="gw-delivery__option-header">
          <span class="gw-delivery__option-name">${option.name}</span>
          <span class="gw-delivery__option-price ${option.is_free ? 'gw-delivery__option-price--free' : ''}">
            ${priceText}
          </span>
        </div>
        <div class="gw-delivery__option-details">
          <span class="gw-delivery__option-days">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            ${daysText}
          </span>
          ${option.pickup_points_count ? `
            <span class="gw-delivery__option-pvz">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
              ${option.pickup_points_count} пунктов
            </span>
          ` : ''}
        </div>
        ${additionalContent}
      </label>
    `;
  }

  /**
   * Attach event listeners
   */
  private attachEventListeners(): void {
    const input = this.container.querySelector<HTMLInputElement>('.gw-delivery__city-input');
    const suggestions = this.container.querySelector<HTMLElement>('.gw-delivery__suggestions');

    if (input) {
      // City input
      input.addEventListener('input', (e) => {
        const query = (e.target as HTMLInputElement).value;
        this.handleCityInput(query);
      });

      input.addEventListener('focus', () => {
        if (suggestions?.children.length) {
          suggestions.classList.add('gw-delivery__suggestions--visible');
        }
      });

      // Close suggestions on outside click
      document.addEventListener('click', (e) => {
        if (!this.container.contains(e.target as Node)) {
          suggestions?.classList.remove('gw-delivery__suggestions--visible');
        }
      });
    }

    // Delegate events for dynamic content
    // preventDefault only on widget-internal buttons to prevent Tilda form submission
    // (widget is injected inside Tilda's <form>, and <button> defaults to type="submit")
    this.container.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const clickedButton = target.closest('button');
      if (clickedButton && clickedButton.closest('.gw-delivery')) {
        e.preventDefault();
      }

      // Suggestion click
      if (target.closest('.gw-delivery__suggestion')) {
        const suggestionEl = target.closest('.gw-delivery__suggestion') as HTMLElement;
        const cityId = suggestionEl.dataset.cityId;
        if (cityId) {
          this.selectCity(parseInt(cityId, 10));
        }
      }

      // Category badge click
      if (target.closest('.gw-delivery__badge') && !target.closest('.gw-delivery__badge--disabled')) {
        const badgeEl = target.closest('.gw-delivery__badge') as HTMLElement;
        const category = badgeEl.dataset.category as DeliveryCategory;
        if (category) {
          this.selectCategory(category);
        }
      }

      // Tariff card click
      if (target.closest('.gw-delivery__tariff-card')) {
        const cardEl = target.closest('.gw-delivery__tariff-card') as HTMLElement;
        const optionId = cardEl.dataset.optionId;
        if (optionId) {
          this.selectOption(optionId);
        }
      }

      // Greenwax pickup select
      if (target.closest('.gw-delivery__greenwax-select')) {
        this.selectGreenwaxPickup();
      }

      // Legacy: Option select (keep for compatibility)
      if (target.closest('.gw-delivery__option')) {
        const optionEl = target.closest('.gw-delivery__option') as HTMLElement;
        const radio = optionEl.querySelector<HTMLInputElement>('input[type="radio"]');
        if (radio) {
          this.selectOption(radio.value);
        }
      }

      // PVZ button click (legacy)
      if (target.matches('.gw-delivery__pvz-btn')) {
        const optionId = target.dataset.optionId;
        if (optionId) {
          this.openPickupMap();
        }
      }

      // Change PVZ (legacy)
      if (target.matches('.gw-delivery__selected-pvz-change')) {
        this.openPickupMap();
      }

      // New PVZ select button click (in tariff card)
      if (target.matches('.gw-delivery__pvz-select-btn')) {
        e.stopPropagation(); // Prevent card click
        this.openPickupMap();
      }

      // Change PVZ button click (in tariff card)
      if (target.matches('.gw-delivery__pvz-change-btn')) {
        e.stopPropagation(); // Prevent card click
        this.openPickupMap();
      }

      // Open PVZ map buttons (pickup prompt + pickup summary change btn)
      if (target.closest('[data-action="open-pvz-map"]')) {
        e.stopPropagation();
        this.openPickupMap();
      }
    });

    // Address form inputs
    this.container.addEventListener('input', (e) => {
      const target = e.target as HTMLInputElement;
      if (target.closest('.gw-delivery__address')) {
        this.handleAddressInput(target);
      }
    });

    // Modal events - modal is on document.body, not container
    // So we need to listen on document for these events
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;

      // Map overlay click (close on background click)
      if (target.matches('.gw-delivery__map-overlay')) {
        this.closePickupMap();
      }

      // Map close button
      if (target.matches('.gw-delivery__map-close')) {
        this.closePickupMap();
      }

      // PVZ item clicks are handled by attachMapFilterEvents (not here)
    });
  }

  /**
   * Handle city input with debounce
   */
  private handleCityInput(query: string): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = window.setTimeout(async () => {
      await this.searchCities(query);
    }, 300);
  }

  /**
   * Search cities
   */
  private async searchCities(query: string): Promise<void> {
    const suggestions = this.container.querySelector<HTMLElement>('.gw-delivery__suggestions');
    if (!suggestions) return;

    if (query.length < 2) {
      suggestions.innerHTML = '';
      suggestions.classList.remove('gw-delivery__suggestions--visible');
      return;
    }

    try {
      const cities = await this.api.searchCities(query);

      if (cities.length === 0) {
        suggestions.innerHTML = '<div class="gw-delivery__suggestion">Город не найден</div>';
      } else {
        const countryInfo: Record<string, { name: string }> = {
          BY: { name: 'Беларусь' },
          KZ: { name: 'Казахстан' },
        };

        suggestions.innerHTML = cities.map(city => {
          const country = countryInfo[city.country_code];
          const countryBadge = country
            ? `<span style="display:inline-block;background:#f0f0f0;color:#555;font-size:11px;font-weight:600;padding:1px 5px;border-radius:3px;margin-right:4px;vertical-align:middle;">${city.country_code}</span>${country.name}`
            : '';
          const regionParts = [
            countryBadge || null,
            city.region,
          ].filter(Boolean).join(', ');

          return `
            <div class="gw-delivery__suggestion" data-city-id="${city.id}" data-city='${JSON.stringify(city)}'>
              <div class="gw-delivery__suggestion-name">${city.name}</div>
              ${regionParts ? `<div class="gw-delivery__suggestion-region">${regionParts}</div>` : ''}
            </div>
          `;
        }).join('');
      }

      suggestions.classList.add('gw-delivery__suggestions--visible');
    } catch (error) {
      console.error('[GW Delivery] Search error:', error);
    }
  }

  /**
   * Select city from suggestions
   */
  private async selectCity(cityId: number): Promise<void> {
    const suggestionEl = this.container.querySelector<HTMLElement>(
      `.gw-delivery__suggestion[data-city-id="${cityId}"]`
    );
    if (!suggestionEl) return;

    const cityData = suggestionEl.dataset.city;
    if (!cityData) return;

    const city: City = JSON.parse(cityData);
    const cityChanged = this.state.city?.cdek_city_id !== city.cdek_city_id;
    this.state.city = city;

    // Save to localStorage
    localStorage.setItem('gw_delivery_city', JSON.stringify(city));

    // Reset delivery selection when city changes (PVZ from old city is invalid)
    if (cityChanged) {
      this.state.selectedOption = null;
      this.state.selectedPickupPoint = null;
      localStorage.removeItem('gw_delivery_selection');
    }

    // Update UI
    this.updateCityInput();

    // Hide suggestions
    const suggestions = this.container.querySelector<HTMLElement>('.gw-delivery__suggestions');
    suggestions?.classList.remove('gw-delivery__suggestions--visible');

    // Calculate delivery
    const cartData = this.getCartData();
    await this.calculateDelivery(cartData);
  }

  /**
   * Update city input value
   */
  private updateCityInput(): void {
    const input = this.container.querySelector<HTMLInputElement>('.gw-delivery__city-input');
    if (input && this.state.city) {
      input.value = this.state.city.display || this.state.city.name;
    }
  }

  /**
   * Select delivery category
   */
  private selectCategory(category: DeliveryCategory): void {
    this.state.activeCategory = category;
    this.state.selectedOption = null;
    this.state.selectedPickupPoint = null;
    this.tariffProviderFilter = 'all'; // Reset provider filter on category change

    // Clear stale delivery price from Tilda when switching categories
    this.clearTildaDeliveryPrice();

    // Update badges UI
    this.updateBadgesUI();

    // Update tariffs UI
    this.updateTariffsUI();

    // Show/hide address form
    const addressContainer = this.container.querySelector<HTMLElement>('.gw-delivery__address-container');
    if (addressContainer) {
      addressContainer.style.display = category === 'courier' ? 'block' : 'none';
    }

    if (category === 'pickup') {
      // ПВЗ: open map immediately, skip tariff card selection
      this.openPickupMap();
    } else if (category === 'greenwax') {
      // Auto-select Greenwax on badge click (no extra "Выбрать" button needed)
      this.selectGreenwaxPickup();
    } else if (category === 'courier') {
      // Auto-select first courier option
      const courierOptions = this.getCourierOptionsForDisplay();
      const firstOption = courierOptions.length > 0
        ? this.state.options.find(o => o.id === courierOptions[0].id)
        : null;
      if (firstOption) {
        this.state.selectedOption = firstOption;
        this.updateTariffsUI();
        this.notifySelection();
        this.syncWithTildaTcart();
        this.updateTildaFields();
        this.updateDeliveryInfoDisplay(firstOption, this.state.city);
        this.updatePriceBreakdown();
      }
    }

    console.log('[GW Delivery] Category selected:', category);
  }

  /**
   * Update badges UI
   */
  private updateBadgesUI(): void {
    const badgesContainer = this.container.querySelector('.gw-delivery__badges-container');
    if (badgesContainer) {
      badgesContainer.innerHTML = this.renderCategoryBadges();
    }
  }

  /**
   * Update tariffs UI
   */
  private updateTariffsUI(): void {
    const tariffsContainer = this.container.querySelector('.gw-delivery__tariffs-container');
    if (tariffsContainer) {
      tariffsContainer.innerHTML = this.renderTariffCards();
    }
  }

  /**
   * Select Greenwax self-pickup
   */
  private selectGreenwaxPickup(): void {
    // Create a fake option for Greenwax pickup
    const greenwaxOption: DeliveryOption = {
      id: 'greenwax_pickup',
      provider: 'greenwax',
      type: 'pickup',
      category: 'greenwax',
      name: 'Самовывоз Greenwax',
      price: 0,
      original_price: 0,
      markup: 0,
      min_days: 1,
      max_days: 1,
      is_free: true,
      pickup_points_count: 1,
    };

    this.state.selectedOption = greenwaxOption;
    this.updateTariffsUI();
    this.notifySelection();
    this.syncWithTildaTcart();
    this.updateTildaFields();
    this.updateDeliveryInfoDisplay(greenwaxOption, this.state.city);
    this.updatePriceBreakdown();

    console.log('[GW Delivery] Greenwax pickup selected');
  }

  /**
   * Update price breakdown display
   */
  private updatePriceBreakdown(): void {
    const option = this.state.selectedOption;
    if (!option) return;

    const deliveryPrice = option.is_free ? 0 : option.price;
    this.ensureDeliveryPriceRow(deliveryPrice);
    this.updateTildaTotal();
  }

  /**
   * Handle address form input
   */
  private handleAddressInput(input: HTMLInputElement): void {
    const fieldName = input.name as keyof CourierAddress;
    if (!fieldName) return;

    if (!this.state.courierAddress) {
      this.state.courierAddress = {
        fullName: '',
        street: '',
        house: '',
        apartment: '',
        comment: '',
      };
    }

    this.state.courierAddress[fieldName] = input.value;

    // Clear validation error highlight on input
    if (input.value) input.style.borderColor = '';

    // Sync with Tilda fields
    this.syncCourierAddressWithTilda();
  }

  /**
   * Sync courier address with Tilda hidden fields
   */
  private syncCourierAddressWithTilda(): void {
    if (!this.state.courierAddress) return;

    const { fullName, street, house, apartment } = this.state.courierAddress;

    // Build full address string
    const addressParts = [street, house, apartment].filter(Boolean);
    const fullAddress = addressParts.join(', ');

    // Update Tilda hidden fields
    const addressInputSelectors = [
      'input[name="Адрес"]',
      'input[name="address"]',
      'input[name="Address"]',
    ];

    addressInputSelectors.forEach(selector => {
      const inputs = document.querySelectorAll<HTMLInputElement>(selector);
      inputs.forEach(input => {
        input.value = fullAddress;
      });
    });

    // Update name field if exists
    const nameInputs = document.querySelectorAll<HTMLInputElement>('input[name="name"], input[name="Name"]');
    nameInputs.forEach(input => {
      if (fullName) {
        input.value = fullName;
      }
    });

    // Update tcart if courier selected
    if (this.state.selectedOption?.type === 'courier') {
      const tcart = (window as any).tcart;
      if (tcart?.__gwDelivery) {
        tcart.__gwDelivery.address = fullAddress;
        tcart.__gwDelivery.recipient_name = fullName;
      }
    }

    // Sync updated address to del_* fields
    this.syncDeliveryToTildaForm();
  }

  /**
   * Calculate delivery options
   */
  async calculateDelivery(cartData: CartData): Promise<void> {
    if (!this.state.city?.cdek_city_id) return;

    // PRESERVE current category and selection before recalculating
    const previousCategory = this.state.activeCategory;
    const previousOptionType = this.state.selectedOption?.type;
    const previousOptionProvider = this.state.selectedOption?.provider;

    this.state.isLoading = true;
    this.state.error = null;
    this.state.options = [];
    // Don't reset selectedOption and activeCategory here - we'll restore or update them after calculation

    // Update badges UI to show loading state
    this.updateBadgesUI();
    this.updateTariffsUI();
    this.updateOptionsUI();

    try {
      const result = await this.api.calculate(
        this.state.city.cdek_city_id,
        this.state.city.country_code,
        cartData
      );

      if (!result.success) {
        this.state.error = result.reason || 'Ошибка расчета доставки';
        this.state.activeCategory = null;
        this.state.selectedOption = null;
        this.clearTildaDeliveryPrice();
      } else if (!result.available) {
        this.state.error = result.reason || 'Доставка в данный город недоступна';
        this.state.activeCategory = null;
        this.state.selectedOption = null;
        this.clearTildaDeliveryPrice();
      } else {
        this.state.options = result.options;

        const isMoscow = this.state.city?.cdek_city_id === MOSCOW_CDEK_ID;
        const hasPickupOptions = result.options.some(o => o.type === 'pickup');
        const hasCourierOptions = result.options.some(o => o.type === 'courier');

        // TRY TO RESTORE from localStorage first (persisted selection)
        let restoredFromStorage = false;
        try {
          const savedSel = localStorage.getItem('gw_delivery_selection');
          if (savedSel && !previousCategory) {
            const sel = JSON.parse(savedSel);
            const savedCat = sel.category as DeliveryCategory;
            const catAvailable =
              (savedCat === 'greenwax' && isMoscow) ||
              (savedCat === 'pickup' && hasPickupOptions) ||
              (savedCat === 'courier' && hasCourierOptions);
            if (catAvailable) {
              this.state.activeCategory = savedCat;
              this.state.options = result.options;
              this.restoreDeliverySelection();
              restoredFromStorage = true;
            }
          }
        } catch { /* ignore */ }

        if (!restoredFromStorage) {
          // TRY TO RESTORE previous in-session category if still available
          let categoryToSelect: DeliveryCategory | null = null;

          if (previousCategory) {
            if (previousCategory === 'greenwax' && isMoscow) {
              categoryToSelect = 'greenwax';
            } else if (previousCategory === 'pickup' && hasPickupOptions) {
              categoryToSelect = 'pickup';
            } else if (previousCategory === 'courier' && hasCourierOptions) {
              categoryToSelect = 'courier';
            }
          }

          // If previous category not available, select default
          if (!categoryToSelect) {
            // Priority: Greenwax (Moscow only) → ПВЗ → Курьер
            if (isMoscow) {
              categoryToSelect = 'greenwax';
            } else if (hasPickupOptions) {
              categoryToSelect = 'pickup';
            } else if (hasCourierOptions) {
              categoryToSelect = 'courier';
            }
          }

          this.state.activeCategory = categoryToSelect;

          // Try to restore or select option in the category
          if (this.state.activeCategory === 'courier') {
            const courierDisplay = this.getCourierOptionsForDisplay();
            let optionToSelect = previousOptionProvider
              ? courierDisplay.find(o => o.provider === previousOptionProvider)
              : null;
            if (!optionToSelect && courierDisplay.length > 0) {
              optionToSelect = courierDisplay[0];
            }
            if (optionToSelect) {
              this.state.selectedOption = this.state.options.find(o => o.id === optionToSelect!.id) || optionToSelect;
              this.state.selectedPickupPoint = null;
              this.notifySelection();
              this.updateDeliveryInfoDisplay(this.state.selectedOption, this.state.city);
              this.updatePriceBreakdown();
            }
          } else if (this.state.activeCategory === 'pickup') {
            const previousPickupPoint = this.state.selectedPickupPoint;
            this.state.selectedOption = null;

            if (previousPickupPoint) {
              // Re-assign cheapest tariff for the preserved PVZ's provider
              const provider = previousPickupPoint.provider || 'cdek';
              const pickupTariffs = this.state.options
                .filter(o => o.type === 'pickup' && o.provider === provider)
                .sort((a, b) => a.price - b.price);
              if (pickupTariffs.length > 0) {
                this.state.selectedOption = pickupTariffs[0];
                this.state.selectedPickupPoint = previousPickupPoint;
                this.notifySelection();
                this.updateDeliveryInfoDisplay(this.state.selectedOption, this.state.city);
                this.updatePriceBreakdown();
              } else {
                this.state.selectedPickupPoint = null;
              }
            }
          } else if (this.state.activeCategory === 'greenwax') {
            this.selectGreenwaxPickup();
          }
        }

        // Show message about free delivery
        this.updateMessage(result.meta);
      }
    } catch (error) {
      console.error('[GW Delivery] Calculate error:', error);
      this.state.error = 'Не удалось рассчитать доставку. Попробуйте позже.';
      this.state.activeCategory = null;
      this.state.selectedOption = null;
      this.clearTildaDeliveryPrice();
    } finally {
      this.state.isLoading = false;

      // Update all UI components
      this.updateBadgesUI();
      this.updateTariffsUI();
      this.updateOptionsUI();

      // Show/hide address form based on category
      const addressContainer = this.container.querySelector<HTMLElement>('.gw-delivery__address-container');
      if (addressContainer) {
        addressContainer.style.display = this.state.activeCategory === 'courier' ? 'block' : 'none';
      }
    }
  }

  /**
   * Select delivery option
   */
  private selectOption(optionId: string): void {
    const option = this.state.options.find(o => o.id === optionId);
    if (!option) return;

    this.state.selectedOption = option;

    // Reset pickup point if switching options
    if (option.type !== 'pickup') {
      this.state.selectedPickupPoint = null;
    }

    // Update all UI
    this.updateOptionsUI();
    this.updateTariffsUI();
    this.notifySelection();
    this.syncWithTildaTcart();
    this.updateTildaFields();
    this.updateDeliveryInfoDisplay(option, this.state.city);
    this.updatePriceBreakdown();

    console.log('[GW Delivery] Option selected:', option.name);
  }

  /**
   * Open pickup points map - loads all providers, with filters and clustering
   */
  private async openPickupMap(): Promise<void> {
    if (!this.state.city?.cdek_city_id) return;

    // Reset filters
    this.pvzProviderFilter = 'all';
    this.pvzTypeFilter = 'all';
    this.pvzSearchQuery = '';
    this.mapSelectedPoint = null;
    this.mapInstance = null;
    this.clusterer = null;

    // Show loading modal
    const loadingModal = document.createElement('div');
    loadingModal.className = 'gw-delivery__map-overlay';
    loadingModal.innerHTML = `
      <div class="gw-delivery__map-modal">
        <div class="gw-delivery__map-header">
          <h4 class="gw-delivery__map-title">Загрузка пунктов выдачи...</h4>
          <button type="button" class="gw-delivery__map-close">&times;</button>
        </div>
        <div class="gw-delivery__map-content" style="display: flex; align-items: center; justify-content: center;">
          <div class="gw-delivery__loading">
            <div class="gw-delivery__spinner"></div>
            <span>Загружаем пункты выдачи...</span>
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(loadingModal);

    // Close button on loading modal
    loadingModal.querySelector('.gw-delivery__map-close')?.addEventListener('click', () => {
      loadingModal.remove();
    });

    // Load pickup points from ALL providers in parallel
    try {
      const results = await Promise.allSettled([
        this.api.getPickupPoints(this.state.city.cdek_city_id),
        this.api.getPickupPointsByProvider('yandex', this.state.city.cdek_city_id, this.state.city.name),
      ]);

      const allPoints: PickupPoint[] = [];

      // CDEK results
      if (results[0].status === 'fulfilled') {
        const cdekPoints = results[0].value.map((p: PickupPoint) => ({
          ...p,
          provider: p.provider || 'cdek',
        }));
        allPoints.push(...cdekPoints);
      } else {
        console.warn('[GW Delivery] Failed to load CDEK points:', results[0].reason);
      }

      // Yandex results (API doesn't return `id`, generate from lat+lng)
      if (results[1].status === 'fulfilled') {
        const yandexPoints = results[1].value.map((p: PickupPoint, idx: number) => ({
          ...p,
          id: p.id || `yandex-${p.lat}-${p.lng}-${idx}`,
          provider: p.provider || 'yandex',
        }));
        allPoints.push(...yandexPoints);
      } else {
        console.warn('[GW Delivery] Failed to load Yandex points:', results[1].reason);
      }

      this.pickupPoints = allPoints;
      this.filteredPoints = [...allPoints];
      console.log('[GW Delivery] Loaded', allPoints.length, 'pickup points total');
    } catch (error) {
      console.error('[GW Delivery] Failed to load pickup points:', error);
      loadingModal.remove();

      const errorModal = document.createElement('div');
      errorModal.className = 'gw-delivery__map-overlay';
      errorModal.innerHTML = `
        <div class="gw-delivery__map-modal">
          <div class="gw-delivery__map-header">
            <h4 class="gw-delivery__map-title">Ошибка загрузки</h4>
            <button type="button" class="gw-delivery__map-close">&times;</button>
          </div>
          <div class="gw-delivery__map-content" style="display:flex;align-items:center;justify-content:center;padding:40px;">
            <div class="gw-delivery__no-options">
              <div class="gw-delivery__no-options-icon"><svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><line x1="8" y1="15" x2="16" y2="15"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg></div>
              <div class="gw-delivery__no-options-text">
                Не удалось загрузить пункты выдачи. Попробуйте позже.
              </div>
            </div>
          </div>
        </div>
      `;
      document.body.appendChild(errorModal);
      errorModal.querySelector('.gw-delivery__map-close')?.addEventListener('click', () => errorModal.remove());
      errorModal.addEventListener('click', (e) => { if (e.target === errorModal) errorModal.remove(); });
      return;
    }

    // Remove loading modal
    loadingModal.remove();

    // If no pickup points found at all
    if (this.pickupPoints.length === 0) {
      const emptyModal = document.createElement('div');
      emptyModal.className = 'gw-delivery__map-overlay';
      emptyModal.innerHTML = `
        <div class="gw-delivery__map-modal">
          <div class="gw-delivery__map-header">
            <h4 class="gw-delivery__map-title">Пункты выдачи</h4>
            <button type="button" class="gw-delivery__map-close">&times;</button>
          </div>
          <div class="gw-delivery__map-content" style="display:flex;align-items:center;justify-content:center;padding:40px;">
            <div class="gw-delivery__no-options">
              <div class="gw-delivery__no-options-icon"><svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/><line x1="12" y1="17" x2="12" y2="17.01"/></svg></div>
              <div class="gw-delivery__no-options-text">
                В этом городе нет доступных пунктов выдачи.
              </div>
            </div>
          </div>
        </div>
      `;
      document.body.appendChild(emptyModal);
      emptyModal.querySelector('.gw-delivery__map-close')?.addEventListener('click', () => emptyModal.remove());
      emptyModal.addEventListener('click', (e) => { if (e.target === emptyModal) emptyModal.remove(); });
      return;
    }

    // Show full map modal with filters
    this.showMapModal();
  }

  /**
   * Show map modal with filters, PVZ list, and map
   */
  private showMapModal(): void {
    const cdekCount = this.pickupPoints.filter(p => p.provider === 'cdek').length;
    const yandexCount = this.pickupPoints.filter(p => p.provider === 'yandex').length;

    const modal = document.createElement('div');
    modal.className = 'gw-delivery__map-overlay';
    modal.innerHTML = `
      <div class="gw-delivery__map-modal">
        <div class="gw-delivery__map-header">
          <h4 class="gw-delivery__map-title">Выберите пункт выдачи</h4>
          <button type="button" class="gw-delivery__map-close">&times;</button>
        </div>
        <div class="gw-delivery__map-filters">
          <div class="gw-delivery__map-filter-group">
            <button type="button" class="gw-delivery__map-filter-btn gw-delivery__map-filter-btn--active" data-filter-provider="all">Все (${this.pickupPoints.length})</button>
            ${cdekCount > 0 ? '<button type="button" class="gw-delivery__map-filter-btn" data-filter-provider="cdek">СДЭК (' + cdekCount + ')</button>' : ''}
            ${yandexCount > 0 ? '<button type="button" class="gw-delivery__map-filter-btn" data-filter-provider="yandex">Яндекс (' + yandexCount + ')</button>' : ''}
          </div>
          <div class="gw-delivery__map-filter-group">
            <button type="button" class="gw-delivery__map-filter-btn gw-delivery__map-filter-btn--active" data-filter-type="all">Все</button>
            <button type="button" class="gw-delivery__map-filter-btn" data-filter-type="pvz">ПВЗ</button>
            <button type="button" class="gw-delivery__map-filter-btn" data-filter-type="postamat">Постаматы</button>
          </div>
          <div class="gw-delivery__map-filter-group" style="flex:1;">
            <input type="text" class="gw-delivery__map-search" placeholder="Поиск по адресу..." />
          </div>
        </div>
        <div class="gw-delivery__map-content">
          <div class="gw-delivery__map-container" id="gw-pvz-map"></div>
          <div class="gw-delivery__pvz-list">
            <div class="gw-delivery__pvz-list-count">Найдено: ${this.filteredPoints.length}</div>
            ${this.renderPvzList()}
          </div>
        </div>
        <div class="gw-delivery__map-selected-pvz" style="display:none;">
          <div class="gw-delivery__map-selected-pvz-info">
            <div class="gw-delivery__map-selected-pvz-name"></div>
            <div class="gw-delivery__map-selected-pvz-address"></div>
            <div class="gw-delivery__map-selected-pvz-time"></div>
          </div>
          <button type="button" class="gw-delivery__map-selected-pvz-btn">Выбрать этот пункт</button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    // Check if mobile and inject bottom sheet
    this.isMobile = window.innerWidth <= 768;
    if (this.isMobile) {
      const mapContent = modal.querySelector('.gw-delivery__map-content');
      if (mapContent) {
        mapContent.insertAdjacentHTML('beforeend', this.renderBottomSheetHTML());
        this.attachBottomSheetEvents(modal);
      }
    }

    this.attachMapFilterEvents(modal);
    this.initYandexMap();
  }

  /**
   * Render PVZ list items HTML
   */
  private renderPvzList(): string {
    if (this.filteredPoints.length === 0) {
      return '<div style="padding:20px;text-align:center;color:#888;">Нет пунктов выдачи по выбранным фильтрам</div>';
    }

    return this.filteredPoints.map(point => {
      const isSelected = this.state.selectedPickupPoint?.id === point.id || this.mapSelectedPoint?.id === point.id;
      const providerLabel = point.provider === 'yandex' ? 'Яндекс' : 'СДЭК';
      const providerClass = point.provider === 'yandex' ? 'gw-delivery__pvz-item-provider--yandex' : 'gw-delivery__pvz-item-provider--cdek';
      const typeLabel = point.type === 'postamat' ? 'Постамат' : 'ПВЗ';
      const typeClass = point.type === 'postamat' ? 'gw-delivery__pvz-item-type-badge--postamat' : 'gw-delivery__pvz-item-type-badge--pvz';

      return `
        <div class="gw-delivery__pvz-item ${isSelected ? 'gw-delivery__pvz-item--selected' : ''}" data-pvz-id="${point.id}">
          <div class="gw-delivery__pvz-item-header">
            <span class="gw-delivery__pvz-item-provider ${providerClass}">${providerLabel}</span>
            <span class="gw-delivery__pvz-item-type-badge ${typeClass}">${typeLabel}</span>
          </div>
          <div class="gw-delivery__pvz-item-name">${point.name}</div>
          <div class="gw-delivery__pvz-item-address">${point.address}</div>
          <div class="gw-delivery__pvz-item-time">${point.work_time}</div>
          ${point.phone ? '<div class="gw-delivery__pvz-item-phone">' + point.phone + '</div>' : ''}
        </div>
      `;
    }).join('');
  }

  /**
   * Apply PVZ filters (provider, type, search)
   */
  private applyPvzFilters(): void {
    this.filteredPoints = this.pickupPoints.filter(point => {
      // Provider filter
      if (this.pvzProviderFilter !== 'all' && point.provider !== this.pvzProviderFilter) {
        return false;
      }
      // Type filter
      if (this.pvzTypeFilter !== 'all') {
        const pointType = point.type || 'pvz';
        if (pointType !== this.pvzTypeFilter) return false;
      }
      // Search query
      if (this.pvzSearchQuery) {
        const q = this.pvzSearchQuery.toLowerCase();
        const haystack = (point.name + ' ' + point.address).toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });

    // Update PVZ list
    const listContainer = document.querySelector('.gw-delivery__pvz-list');
    if (listContainer) {
      listContainer.innerHTML = `
        <div class="gw-delivery__pvz-list-count">Найдено: ${this.filteredPoints.length}</div>
        ${this.renderPvzList()}
      `;
      // Re-attach click events on PVZ items
      listContainer.querySelectorAll('.gw-delivery__pvz-item').forEach(item => {
        item.addEventListener('click', () => {
          const pvzId = item.getAttribute('data-pvz-id');
          if (pvzId) this.highlightPvzOnMap(pvzId);
        });
      });
    }

    // Update bottom sheet list on mobile
    if (this.isMobile) {
      this.updateBottomSheetList();
    }

    // Update map placemarks
    this.updateMapPlacemarks();
  }

  /**
   * Attach event listeners to map filter controls
   */
  private attachMapFilterEvents(modal: HTMLElement): void {
    // Close button
    modal.querySelector('.gw-delivery__map-close')?.addEventListener('click', () => this.closePickupMap());

    // Click overlay to close
    modal.addEventListener('click', (e) => {
      if (e.target === modal) this.closePickupMap();
    });

    // Provider filter buttons
    modal.querySelectorAll('[data-filter-provider]').forEach(btn => {
      btn.addEventListener('click', () => {
        const provider = btn.getAttribute('data-filter-provider') as PvzProviderFilter;
        this.pvzProviderFilter = provider;
        modal.querySelectorAll('[data-filter-provider]').forEach(b => b.classList.remove('gw-delivery__map-filter-btn--active'));
        btn.classList.add('gw-delivery__map-filter-btn--active');
        this.applyPvzFilters();
      });
    });

    // Type filter buttons
    modal.querySelectorAll('[data-filter-type]').forEach(btn => {
      btn.addEventListener('click', () => {
        const type = btn.getAttribute('data-filter-type') as PvzTypeFilter;
        this.pvzTypeFilter = type;
        modal.querySelectorAll('[data-filter-type]').forEach(b => b.classList.remove('gw-delivery__map-filter-btn--active'));
        btn.classList.add('gw-delivery__map-filter-btn--active');
        this.applyPvzFilters();
      });
    });

    // Search input
    const searchInput = modal.querySelector('.gw-delivery__map-search') as HTMLInputElement;
    if (searchInput) {
      let searchTimer: number | null = null;
      searchInput.addEventListener('input', () => {
        if (searchTimer) clearTimeout(searchTimer);
        searchTimer = window.setTimeout(() => {
          this.pvzSearchQuery = searchInput.value.trim();
          this.applyPvzFilters();
        }, 300);
      });
    }

    // PVZ list item clicks
    modal.querySelectorAll('.gw-delivery__pvz-item').forEach(item => {
      item.addEventListener('click', () => {
        const pvzId = item.getAttribute('data-pvz-id');
        if (pvzId) this.highlightPvzOnMap(pvzId);
      });
    });

    // Confirm selection button
    modal.querySelector('.gw-delivery__map-selected-pvz-btn')?.addEventListener('click', () => {
      this.confirmPickupSelection();
    });
  }

  /**
   * Highlight a PVZ on the map and in the list
   */
  private highlightPvzOnMap(pointId: string): void {
    const point = this.pickupPoints.find(p => p.id === pointId);
    if (!point) return;

    this.mapSelectedPoint = point;

    // Update list selection
    document.querySelectorAll('.gw-delivery__pvz-item').forEach(item => {
      item.classList.remove('gw-delivery__pvz-item--selected');
      if (item.getAttribute('data-pvz-id') === pointId) {
        item.classList.add('gw-delivery__pvz-item--selected');
        item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    });

    // Center map on point
    if (this.mapInstance) {
      this.mapInstance.setCenter([point.lat, point.lng], 15, { duration: 300 });

      // On desktop: open the placemark balloon after map animation finishes
      if (!this.isMobile && this.clusterer) {
        const openBalloonForPoint = () => {
          const geoObjects = this.clusterer.getGeoObjects();
          for (const obj of geoObjects) {
            const coords = obj.geometry.getCoordinates();
            if (Math.abs(coords[0] - point.lat) < 0.0001 && Math.abs(coords[1] - point.lng) < 0.0001) {
              obj.balloon.open();
              return;
            }
          }
        };
        // Wait for map animation + clusterer update
        setTimeout(openBalloonForPoint, 400);
      }
    }

    // Show selected PVZ panel (mobile: bottom sheet; desktop: panel)
    if (this.isMobile) {
      this.showBottomSheetPvzInfo(point);
    } else {
      this.showSelectedPvzPanel(point);
    }
  }

  /**
   * Show selected PVZ info panel at bottom of modal
   */
  private showSelectedPvzPanel(point: PickupPoint): void {
    if (this.isMobile) return;

    const panel = document.querySelector('.gw-delivery__map-selected-pvz') as HTMLElement;
    if (!panel) return;

    const nameEl = panel.querySelector('.gw-delivery__map-selected-pvz-name');
    const addrEl = panel.querySelector('.gw-delivery__map-selected-pvz-address');
    const timeEl = panel.querySelector('.gw-delivery__map-selected-pvz-time');

    if (nameEl) nameEl.textContent = point.name;
    if (addrEl) addrEl.textContent = point.address;
    if (timeEl) timeEl.textContent = point.work_time;

    panel.style.display = 'flex';

    // On mobile: collapse PVZ list to give map more space
    const mapContent = document.querySelector('.gw-delivery__map-content');
    mapContent?.classList.add('gw-delivery__map-content--pvz-selected');
  }

  /**
   * Confirm pickup point selection and close map.
   * Auto-assigns the cheapest matching tariff based on the PVZ's provider.
   */
  private confirmPickupSelection(): void {
    if (!this.mapSelectedPoint) return;

    this.state.selectedPickupPoint = this.mapSelectedPoint;

    // Auto-assign cheapest pickup tariff for the selected PVZ's provider
    const provider = this.mapSelectedPoint.provider || 'cdek';
    const pickupTariffs = this.state.options
      .filter(o => o.type === 'pickup' && o.provider === provider)
      .sort((a, b) => a.price - b.price);

    if (pickupTariffs.length > 0) {
      this.state.selectedOption = pickupTariffs[0];
      console.log('[GW Delivery] Auto-assigned tariff for', provider, ':', pickupTariffs[0].name, pickupTariffs[0].price, '₽');
    } else {
      // Fallback: use cheapest pickup tariff from any provider
      const anyPickup = this.state.options
        .filter(o => o.type === 'pickup')
        .sort((a, b) => a.price - b.price);
      if (anyPickup.length > 0) {
        this.state.selectedOption = anyPickup[0];
        console.log('[GW Delivery] Fallback tariff:', anyPickup[0].name, anyPickup[0].price, '₽');
      }
    }

    this.closePickupMap();
    this.updateTariffsUI();
    this.updateOptionsUI();
    this.notifySelection(); // Already calls syncWithTildaTcart + updateTildaFields
    if (this.state.selectedOption) {
      this.updateDeliveryInfoDisplay(this.state.selectedOption, this.state.city);
      this.updatePriceBreakdown();
    }
  }

  /**
   * Update map placemarks after filter change
   */
  private updateMapPlacemarks(): void {
    if (!this.mapInstance || !this.clusterer) return;

    const ymaps = (window as any).ymaps;
    if (!ymaps) return;

    // Clear existing placemarks
    this.clusterer.removeAll();

    // Create new placemarks for filtered points
    const placemarks = this.createPlacemarks(ymaps);
    this.clusterer.add(placemarks);

    // Fit bounds if there are points
    if (this.filteredPoints.length > 0) {
      const bounds = this.clusterer.getBounds();
      if (bounds) {
        this.mapInstance.setBounds(bounds, {
          checkZoomRange: true,
          zoomMargin: 40,
          duration: 300,
        });
      }
    }
  }

  /**
   * Create ymaps.Placemark objects for filtered points
   */
  private createPlacemarks(ymaps: any): any[] {
    return this.filteredPoints.map(point => {
      // Custom pin icons per provider
      const pinIcon = point.provider === 'yandex'
        ? 'https://cdn.greenwax.ru/delivery/pin-yandex.png'
        : 'https://cdn.greenwax.ru/delivery/pin-cdek.png';

      const providerLabel = point.provider === 'yandex' ? 'Яндекс' : 'СДЭК';
      const typeLabel = point.type === 'postamat' ? 'Постамат' : 'ПВЗ';
      const phoneHtml = point.phone ? '<div style="color:#666;font-size:12px;margin-top:4px;">' + point.phone + '</div>' : '';

      const providerBg = point.provider === 'yandex' ? '#fff8e1' : '#e8f5e9';
      const providerColor = point.provider === 'yandex' ? '#f57f17' : '#2e7d32';
      const typeBg = point.type === 'postamat' ? '#f3e5f5' : '#e3f2fd';
      const typeColor = point.type === 'postamat' ? '#7b1fa2' : '#1565c0';

      const placemark = new ymaps.Placemark(
        [point.lat, point.lng],
        {
          balloonContentHeader: '',
          balloonContentBody:
            '<div class="gw-balloon">' +
            '<div class="gw-balloon__badges">' +
            '<span class="gw-balloon__badge" style="background:' + providerBg + ';color:' + providerColor + ';">' + providerLabel + '</span>' +
            '<span class="gw-balloon__badge" style="background:' + typeBg + ';color:' + typeColor + ';">' + typeLabel + '</span>' +
            '</div>' +
            '<div class="gw-balloon__name">' + point.name + '</div>' +
            '<div class="gw-balloon__address">' + point.address + '</div>' +
            '<div class="gw-balloon__time">' + point.work_time + '</div>' +
            (point.phone ? '<div class="gw-balloon__phone">' + point.phone + '</div>' : '') +
            '<button type="button" class="gw-balloon__btn" onclick="var e=event;e.preventDefault();e.stopPropagation();document.dispatchEvent(new CustomEvent(\'gw-select-pvz\',{detail:{id:\'' + point.id + '\'}}))">' +
            'Выбрать этот пункт</button></div>',
          hintContent: providerLabel + ': ' + point.name,
        },
        {
          iconLayout: 'default#image',
          iconImageHref: pinIcon,
          iconImageSize: [32, 32],
          iconImageOffset: [-16, -32],
        }
      );

      // Click on placemark → highlight in list and show selection panel
      placemark.events.add('click', () => {
        this.highlightPvzOnMap(point.id);
        // On mobile: close balloon if it somehow opened, use bottom sheet instead
        if (this.isMobile) {
          try { placemark.balloon.close(); } catch(_) {}
        }
      });

      // Balloon open → attach direct click handler to button (backup for inline onclick)
      placemark.events.add('balloonopen', () => {
        // On mobile: close balloon immediately, bottom sheet handles selection
        if (this.isMobile) {
          try { placemark.balloon.close(); } catch(_) {}
          return;
        }
        setTimeout(() => {
          // Use broad selector — ymaps version in class name changes
          const balloonBtns = document.querySelectorAll('[class*="balloon"] button');
          balloonBtns.forEach(btn => {
            btn.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              this.mapSelectedPoint = point;
              this.confirmPickupSelection();
            });
          });
        }, 150);
      });

      return placemark;
    });
  }

  /**
   * Initialize Yandex Maps with clustering
   */
  private async initYandexMap(): Promise<void> {
    const mapContainer = document.getElementById('gw-pvz-map');
    if (!mapContainer) return;

    // Show loading state
    mapContainer.innerHTML = `
      <div style="display:flex;align-items:center;justify-content:center;height:100%;color:#666;">
        <div class="gw-delivery__spinner" style="margin-right:10px;"></div>
        <span>Загрузка карты...</span>
      </div>
    `;

    // Load Yandex Maps API if not loaded
    const GWDelivery = (window as any).GWDelivery;
    if (GWDelivery?.loadYandexMaps) {
      await GWDelivery.loadYandexMaps();
    }

    // Check if ymaps is available
    if (typeof (window as any).ymaps === 'undefined') {
      mapContainer.innerHTML = '<div style="padding:20px;text-align:center;color:#666;">Карта недоступна. Выберите ПВЗ из списка справа.</div>';
      return;
    }

    const ymaps = (window as any).ymaps;

    ymaps.ready(() => {
      const container = document.getElementById('gw-pvz-map');
      if (!container) return;

      container.innerHTML = '';

      try {
        // Determine center from first point or default Moscow
        const center = this.filteredPoints[0]
          ? [this.filteredPoints[0].lat, this.filteredPoints[0].lng]
          : [55.751244, 37.618423];

        this.mapInstance = new ymaps.Map('gw-pvz-map', {
          center,
          zoom: 11,
          controls: ['zoomControl', 'geolocationControl'],
        });

        // Create clusterer
        this.clusterer = new ymaps.Clusterer({
          preset: 'islands#greenClusterIcons',
          groupByCoordinates: false,
          clusterDisableClickZoom: false,
          clusterHideIconOnBalloonOpen: false,
          geoObjectHideIconOnBalloonOpen: false,
          clusterBalloonContentLayout: 'cluster#balloonCarousel',
          clusterOpenBalloonOnClick: true,
        });

        // Create placemarks and add to clusterer
        const placemarks = this.createPlacemarks(ymaps);
        this.clusterer.add(placemarks);
        this.mapInstance.geoObjects.add(this.clusterer);

        // Register CustomEvent listener for balloon button clicks (works across all contexts)
        const pvzHandler = ((e: CustomEvent) => {
          const point = this.pickupPoints.find(p => p.id === e.detail.id);
          if (point) {
            this.mapSelectedPoint = point;
            this.confirmPickupSelection();
          }
        }) as EventListener;
        document.addEventListener('gw-select-pvz', pvzHandler);
        (this as any).__gwPvzHandler = pvzHandler;

        // Fit bounds to show all points
        if (this.filteredPoints.length > 1) {
          const bounds = this.clusterer.getBounds();
          if (bounds) {
            this.mapInstance.setBounds(bounds, {
              checkZoomRange: true,
              zoomMargin: 40,
            });
          }
        }

        // Geolocation disabled: user has already selected a city,
        // map is centered on PVZ points in that city.
        // Auto-centering on device location would move the map away.

        console.log('[GW Delivery] Map initialized with', this.filteredPoints.length, 'points (clustered)');
      } catch (error) {
        console.error('[GW Delivery] Failed to initialize Yandex Map:', error);
        container.innerHTML = '<div style="padding:20px;text-align:center;color:#666;">Ошибка загрузки карты. Выберите ПВЗ из списка справа.</div>';
      }
    });
  }

  /**
   * Close pickup map modal and cleanup
   */
  private closePickupMap(): void {
    // Clean up PVZ selection event listener
    if ((this as any).__gwPvzHandler) {
      document.removeEventListener('gw-select-pvz', (this as any).__gwPvzHandler);
      delete (this as any).__gwPvzHandler;
    }

    // Destroy map
    if (this.mapInstance) {
      try { this.mapInstance.destroy(); } catch (e) { /* ignore */ }
      this.mapInstance = null;
    }
    this.clusterer = null;
    this.mapSelectedPoint = null;

    // Reset mobile state
    this.bottomSheetState = 'collapsed';
    this.isMobile = false;

    // Remove modal
    const modal = document.querySelector('.gw-delivery__map-overlay');
    modal?.remove();
  }

  // ===== MOBILE BOTTOM SHEET METHODS =====

  /**
   * Render bottom sheet HTML for mobile map view
   */
  private renderBottomSheetHTML(): string {
    const cdekCount = this.pickupPoints.filter(p => p.provider === 'cdek').length;
    const yandexCount = this.pickupPoints.filter(p => p.provider === 'yandex').length;

    return `
      <div class="gw-delivery__mobile-filters">
        <button type="button" class="gw-delivery__mobile-filter-btn gw-delivery__mobile-filter-btn--active" data-mobile-filter="all">Все (${this.pickupPoints.length})</button>
        ${cdekCount > 0 ? '<button type="button" class="gw-delivery__mobile-filter-btn" data-mobile-filter="cdek">СДЭК (' + cdekCount + ')</button>' : ''}
        ${yandexCount > 0 ? '<button type="button" class="gw-delivery__mobile-filter-btn" data-mobile-filter="yandex">Яндекс (' + yandexCount + ')</button>' : ''}
      </div>
      <button class="gw-delivery__map-close-float" type="button">&times;</button>
      <div class="gw-delivery__bottom-sheet gw-delivery__bottom-sheet--collapsed">
        <div class="gw-delivery__bottom-sheet-drag-zone">
          <div class="gw-delivery__bottom-sheet-handle"></div>
          <div class="gw-delivery__bottom-sheet-search-wrap">
            <input type="text" class="gw-delivery__bottom-sheet-search" placeholder="Поиск по адресу..." />
          </div>
        </div>
        <div class="gw-delivery__bottom-sheet-list"></div>
        <div class="gw-delivery__bottom-sheet-pvz-info" style="display:none">
          <div class="gw-delivery__bottom-sheet-pvz-name"></div>
          <div class="gw-delivery__bottom-sheet-pvz-address"></div>
          <div class="gw-delivery__bottom-sheet-pvz-time"></div>
          <button type="button" class="gw-delivery__bottom-sheet-pvz-btn">Выбрать этот пункт</button>
        </div>
      </div>
    `;
  }

  /**
   * Attach event listeners for mobile bottom sheet
   */
  private attachBottomSheetEvents(modal: HTMLElement): void {
    // Floating close button
    modal.querySelector('.gw-delivery__map-close-float')?.addEventListener('click', () => {
      this.closePickupMap();
    });

    // Mobile filter badges
    modal.querySelectorAll('[data-mobile-filter]').forEach(btn => {
      btn.addEventListener('click', () => {
        const provider = btn.getAttribute('data-mobile-filter') as PvzProviderFilter;
        this.pvzProviderFilter = provider;
        modal.querySelectorAll('[data-mobile-filter]').forEach(b => b.classList.remove('gw-delivery__mobile-filter-btn--active'));
        btn.classList.add('gw-delivery__mobile-filter-btn--active');
        // Also sync desktop filter buttons
        modal.querySelectorAll('[data-filter-provider]').forEach(b => {
          b.classList.remove('gw-delivery__map-filter-btn--active');
          if (b.getAttribute('data-filter-provider') === provider) b.classList.add('gw-delivery__map-filter-btn--active');
        });
        this.applyPvzFilters();
      });
    });

    // Search input focus → expand
    const searchInput = modal.querySelector('.gw-delivery__bottom-sheet-search') as HTMLInputElement;
    if (searchInput) {
      searchInput.addEventListener('focus', () => {
        this.setBottomSheetState('expanded');
      });

      // Debounced search filtering
      let bsSearchTimer: number | null = null;
      searchInput.addEventListener('input', () => {
        if (bsSearchTimer) clearTimeout(bsSearchTimer);
        bsSearchTimer = window.setTimeout(() => {
          this.pvzSearchQuery = searchInput.value.trim();
          this.applyPvzFilters();
        }, 300);
      });
    }

    // "Выбрать этот пункт" button in bottom sheet
    modal.querySelector('.gw-delivery__bottom-sheet-pvz-btn')?.addEventListener('click', () => {
      this.confirmPickupSelection();
    });

    // Attach drag gesture on sheet handle
    const sheet = modal.querySelector('.gw-delivery__bottom-sheet') as HTMLElement;
    if (sheet) {
      this.attachBottomSheetDrag(sheet);
    }
  }

  /**
   * Touch drag gesture handler for bottom sheet
   */
  private attachBottomSheetDrag(sheet: HTMLElement): void {
    const dragZone = sheet.querySelector('.gw-delivery__bottom-sheet-drag-zone') as HTMLElement;
    if (!dragZone) return;

    let startY = 0;
    let isDragging = false;

    dragZone.addEventListener('touchstart', (e: TouchEvent) => {
      // Don't interfere with input focus
      if ((e.target as HTMLElement).tagName === 'INPUT') return;
      startY = e.touches[0].clientY;
      isDragging = true;
      sheet.style.transition = 'none';
    }, { passive: true });

    dragZone.addEventListener('touchmove', (e: TouchEvent) => {
      if (!isDragging) return;
      const deltaY = e.touches[0].clientY - startY;
      // Apply real-time transform for visual drag feedback
      if (deltaY > 0) {
        sheet.style.transform = `translateY(${deltaY}px)`;
      } else {
        // Dragging up — slight resistance
        sheet.style.transform = `translateY(${deltaY * 0.3}px)`;
      }
    }, { passive: true });

    dragZone.addEventListener('touchend', (e: TouchEvent) => {
      if (!isDragging) return;
      isDragging = false;
      const endY = e.changedTouches[0].clientY;
      const deltaY = endY - startY;

      // Reset transform
      sheet.style.transform = '';
      sheet.style.transition = '';

      if (deltaY > 50) {
        // Swipe down → collapse
        this.setBottomSheetState('collapsed');
      } else if (deltaY < -50) {
        // Swipe up → expand
        this.setBottomSheetState('expanded');
      }
      // else snap back (no state change, CSS transition handles reset)
    }, { passive: true });
  }

  /**
   * Set bottom sheet state and update UI
   */
  private setBottomSheetState(state: 'collapsed' | 'expanded' | 'pvz-selected'): void {
    this.bottomSheetState = state;

    const sheet = document.querySelector('.gw-delivery__bottom-sheet') as HTMLElement;
    if (!sheet) return;

    // Remove all state classes
    sheet.classList.remove(
      'gw-delivery__bottom-sheet--collapsed',
      'gw-delivery__bottom-sheet--expanded',
      'gw-delivery__bottom-sheet--pvz-selected'
    );
    sheet.classList.add(`gw-delivery__bottom-sheet--${state}`);

    const listEl = sheet.querySelector('.gw-delivery__bottom-sheet-list') as HTMLElement;
    const infoEl = sheet.querySelector('.gw-delivery__bottom-sheet-pvz-info') as HTMLElement;

    if (state === 'expanded') {
      if (listEl) { listEl.style.display = ''; }
      if (infoEl) { infoEl.style.display = 'none'; }
      this.updateBottomSheetList();
    } else if (state === 'pvz-selected') {
      if (listEl) { listEl.style.display = 'none'; }
      if (infoEl) { infoEl.style.display = ''; }
    } else {
      // collapsed
      if (listEl) { listEl.style.display = 'none'; }
      if (infoEl) { infoEl.style.display = 'none'; }
    }
  }

  /**
   * Populate the bottom sheet PVZ list
   */
  private updateBottomSheetList(): void {
    const listEl = document.querySelector('.gw-delivery__bottom-sheet-list') as HTMLElement;
    if (!listEl) return;

    listEl.innerHTML = this.renderPvzList();

    // Attach click events — single tap directly shows PVZ info
    listEl.querySelectorAll('.gw-delivery__pvz-item').forEach(item => {
      item.addEventListener('click', () => {
        const pvzId = item.getAttribute('data-pvz-id');
        if (!pvzId) return;
        const point = this.pickupPoints.find(p => p.id === pvzId);
        if (!point) return;

        // Set selection and center map (no balloon)
        this.mapSelectedPoint = point;
        if (this.mapInstance) {
          this.mapInstance.setCenter([point.lat, point.lng], 15, { duration: 300 });
        }

        // Directly show info card in bottom sheet
        this.showBottomSheetPvzInfo(point);
      });
    });
  }

  /**
   * Show PVZ info card in mobile bottom sheet
   */
  private showBottomSheetPvzInfo(point: PickupPoint): void {
    const sheet = document.querySelector('.gw-delivery__bottom-sheet') as HTMLElement;
    if (!sheet) return;

    const infoEl = sheet.querySelector('.gw-delivery__bottom-sheet-pvz-info') as HTMLElement;
    if (!infoEl) return;

    const providerLabel = point.provider === 'yandex' ? 'Яндекс' : 'СДЭК';
    const providerClass = point.provider === 'yandex' ? 'gw-delivery__pvz-item-provider--yandex' : 'gw-delivery__pvz-item-provider--cdek';
    const typeLabel = point.type === 'postamat' ? 'Постамат' : 'ПВЗ';
    const typeClass = point.type === 'postamat' ? 'gw-delivery__pvz-item-type-badge--postamat' : 'gw-delivery__pvz-item-type-badge--pvz';
    const phoneHtml = point.phone ? '<div class="gw-delivery__pvz-item-phone">' + point.phone + '</div>' : '';

    // Render same structure as list item for consistency
    infoEl.innerHTML = `
      <div class="gw-delivery__pvz-item-header" style="margin-bottom:6px;">
        <span class="gw-delivery__pvz-item-provider ${providerClass}">${providerLabel}</span>
        <span class="gw-delivery__pvz-item-type-badge ${typeClass}">${typeLabel}</span>
      </div>
      <div class="gw-delivery__bottom-sheet-pvz-name">${point.name}</div>
      <div class="gw-delivery__bottom-sheet-pvz-address">${point.address}</div>
      <div class="gw-delivery__bottom-sheet-pvz-time">${point.work_time}</div>
      ${phoneHtml}
      <button type="button" class="gw-delivery__bottom-sheet-pvz-btn">Выбрать этот пункт</button>
    `;

    // Re-attach confirm button event
    infoEl.querySelector('.gw-delivery__bottom-sheet-pvz-btn')?.addEventListener('click', () => {
      this.confirmPickupSelection();
    });

    this.setBottomSheetState('pvz-selected');
  }

  /**
   * Select pickup point
   */
  private selectPickupPoint(pointId: string): void {
    const point = this.pickupPoints.find(p => p.id === pointId);
    if (!point) return;

    this.state.selectedPickupPoint = point;
    this.closePickupMap();
    this.updateOptionsUI();
    this.notifySelection();
  }

  /**
   * Update options container
   */
  private updateOptionsUI(): void {
    const container = this.container.querySelector('.gw-delivery__options-container');
    if (container) {
      container.innerHTML = this.renderOptions();
    }
  }

  /**
   * Update message container
   */
  private updateMessage(meta: { remaining_for_free: number | null; free_delivery_threshold?: number | null }): void {
    const container = this.container.querySelector('.gw-delivery__message-container');
    if (!container) return;

    if (meta.remaining_for_free && meta.remaining_for_free > 0) {
      container.innerHTML = `
        <div class="gw-delivery__message">
          <div class="gw-delivery__message-content">
            <svg class="gw-delivery__message-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
            <span class="gw-delivery__message-text">До бесплатной доставки осталось ${meta.remaining_for_free.toLocaleString('ru-RU')} ₽</span>
          </div>
          <div class="gw-delivery__progress-bar">
            <div class="gw-delivery__progress-fill" style="width: ${Math.min(100, Math.round(((meta.free_delivery_threshold || 5000) - (meta.remaining_for_free || 0)) / (meta.free_delivery_threshold || 5000) * 100))}%"></div>
          </div>
        </div>
      `;
    } else {
      container.innerHTML = '';
    }
  }

  /**
   * Notify about selection change
   */
  private notifySelection(): void {
    const option = this.state.selectedOption;
    if (!option) return;

    // Call config callback
    if (this.config.onSelect) {
      this.config.onSelect(option, this.state.selectedPickupPoint || undefined);
    }

    // Dispatch custom event
    document.dispatchEvent(new CustomEvent('gw:delivery:selected', {
      detail: {
        option,
        pickupPoint: this.state.selectedPickupPoint,
        city: this.state.city,
      },
    }));

    // Sync with Tilda's tcart object
    this.syncWithTildaTcart();

    // Update Tilda hidden fields
    this.updateTildaFields();

    // Save current selection to localStorage buffer
    this.saveToBuffer();

    // Persist delivery selection for reload restoration
    this.saveDeliverySelection();
  }

  /**
   * Save delivery selection to localStorage for reload persistence
   */
  private saveDeliverySelection(): void {
    try {
      const selection: any = {
        category: this.state.activeCategory,
        optionId: this.state.selectedOption?.id,
        optionProvider: this.state.selectedOption?.provider,
        optionType: this.state.selectedOption?.type,
        cityId: this.state.city?.cdek_city_id,
      };
      if (this.state.selectedPickupPoint) {
        selection.pickupPoint = {
          id: this.state.selectedPickupPoint.id,
          name: this.state.selectedPickupPoint.name,
          address: this.state.selectedPickupPoint.address,
          provider: this.state.selectedPickupPoint.provider,
          type: this.state.selectedPickupPoint.type,
          lat: this.state.selectedPickupPoint.lat,
          lng: this.state.selectedPickupPoint.lng,
          work_time: this.state.selectedPickupPoint.work_time,
        };
      }
      if (this.state.courierAddress) {
        selection.courierAddress = this.state.courierAddress;
      }
      localStorage.setItem('gw_delivery_selection', JSON.stringify(selection));
    } catch (e) { /* ignore */ }
  }

  /**
   * Restore delivery selection from localStorage after calculation
   */
  private restoreDeliverySelection(): void {
    try {
      const raw = localStorage.getItem('gw_delivery_selection');
      if (!raw) return;
      const sel = JSON.parse(raw);

      // Don't restore if city changed (PVZ from old city is invalid)
      if (sel.cityId && sel.cityId !== this.state.city?.cdek_city_id) {
        localStorage.removeItem('gw_delivery_selection');
        return;
      }

      // Restore category
      if (sel.category && sel.category !== this.state.activeCategory) {
        this.state.activeCategory = sel.category;
      }

      // Restore option
      if (sel.category === 'greenwax') {
        this.selectGreenwaxPickup();
        return;
      }

      if (sel.optionId) {
        const option = this.state.options.find(o => o.id === sel.optionId)
          || this.state.options.find(o => o.provider === sel.optionProvider && o.type === sel.optionType);
        if (option) {
          this.state.selectedOption = option;
        }
      }

      // Restore pickup point
      if (sel.pickupPoint) {
        this.state.selectedPickupPoint = sel.pickupPoint;
      }

      // Restore courier address
      if (sel.courierAddress) {
        this.state.courierAddress = sel.courierAddress;
      }

      // Update UI and sync
      if (this.state.selectedOption) {
        this.notifySelection();
        this.updateDeliveryInfoDisplay(this.state.selectedOption, this.state.city);
        this.updatePriceBreakdown();
      }
    } catch (e) { /* ignore */ }
  }

  /**
   * Sync selected delivery option with window.tcart
   * This ensures Tilda's internal state knows about our delivery selection
   */
  private syncWithTildaTcart(): void {
    const option = this.state.selectedOption;
    const city = this.state.city;
    const tcart = (window as any).tcart;

    if (!option || !tcart) return;

    const deliveryPrice = option.is_free ? 0 : option.price;

    // IMPORTANT: Do NOT set tcart.delivery with a hash — Tilda interprets it as
    // its own delivery selection and triggers auto-redirect to payment.
    // Instead, store our delivery data in a custom property.
    tcart.__gwDelivery = {
      name: option.name,
      price: deliveryPrice,
      type: option.type,
      provider: option.provider,
      city: city?.name || '',
      country: city?.country_code?.toLowerCase() || 'ru',
      min_days: option.min_days,
      max_days: option.max_days,
    };

    if (this.state.selectedPickupPoint) {
      tcart.__gwDelivery.pvz = {
        name: this.state.selectedPickupPoint.name,
        address: this.state.selectedPickupPoint.address,
        id: this.state.selectedPickupPoint.id,
      };
    }

    // CRITICAL: Update tcart.amount to include delivery!
    const prodAmount = parseFloat(tcart.prodamount) || 0;
    tcart.amount = prodAmount + deliveryPrice;

    // Store references for later use
    tcart.__gwDeliveryOption = option;
    tcart.__gwCity = city;
    tcart.__gwDeliveryPrice = deliveryPrice;

    console.log('[GW Delivery] Synced with tcart:', {
      __gwDelivery: tcart.__gwDelivery,
      prodamount: tcart.prodamount,
      amount: tcart.amount,
    });
  }

  /**
   * Clear delivery price from Tilda when delivery is unavailable
   */
  private clearTildaDeliveryPrice(): void {
    const tcart = (window as any).tcart;
    if (!tcart) return;

    const prodAmount = parseFloat(tcart.prodamount) || 0;
    tcart.amount = prodAmount;
    tcart.__gwDeliveryPrice = 0;
    tcart.__gwDelivery = null;
    tcart.__gwDeliveryOption = null;

    // Hide stale delivery rows now and after Tilda redraws
    this.hideStaleDeliveryRows();
    [100, 300, 600].forEach(delay => {
      setTimeout(() => this.hideStaleDeliveryRows(), delay);
    });
  }

  /**
   * Hide stale Tilda delivery rows and our breakdown when no option is selected
   */
  hideStaleDeliveryRows(): void {
    const totalsContainer = document.querySelector('.t706__cartpage-totals');
    if (!totalsContainer) return;

    // Hide Tilda's own delivery rows
    const selectors = [
      '.t706__cartpage-delivery',
      '.t706__cartwin-delivery',
    ];
    selectors.forEach(sel => {
      const el = totalsContainer.querySelector(sel);
      if (el) (el as HTMLElement).style.display = 'none';
    });

    // Hide stale text containing delivery names
    totalsContainer.querySelectorAll('*').forEach(el => {
      const text = el.textContent?.trim() || '';
      if ((text.includes('СДЭК') || text.includes('Яндекс') || text.includes('Курьер') || text.includes('Постамат'))
          && !el.closest('#gw-price-breakdown')
          && !el.closest('.t706__cartwin-totalamount-wrap')) {
        const isDirectContainer = el.childNodes.length <= 3;
        if (isDirectContainer && el.tagName !== 'BODY' && el.tagName !== 'HTML') {
          (el as HTMLElement).style.display = 'none';
        }
      }
    });

    // Remove our breakdown (will be re-created when option is selected)
    const breakdown = document.getElementById('gw-price-breakdown');
    if (breakdown) breakdown.remove();
  }

  /**
   * Generate a simple hash for delivery selection (for Tilda compatibility)
   */
  private generateDeliveryHash(option: DeliveryOption, city: City | null): string {
    const str = `gw-${option.id}-${city?.cdek_city_id || 0}-${option.price}`;
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(16);
  }

  /**
   * Update Tilda hidden fields
   */
  private updateTildaFields(): void {
    const option = this.state.selectedOption;
    const city = this.state.city;
    if (!option) return;

    const deliveryPrice = option.is_free ? 0 : option.price;

    // Find and update delivery price field
    const priceInputs = document.querySelectorAll<HTMLInputElement>(
      'input[name="delivery_price"], input[name="Delivery"], input[name="delivery"]'
    );
    priceInputs.forEach(input => {
      input.value = String(deliveryPrice);
    });

    // Find and update delivery type field
    const typeInputs = document.querySelectorAll<HTMLInputElement>(
      'input[name="delivery_type"], input[name="Способ доставки"]'
    );
    typeInputs.forEach(input => {
      let value = option.name;
      if (this.state.selectedPickupPoint) {
        value += ` (${this.state.selectedPickupPoint.address})`;
      }
      input.value = value;
    });

    // CRITICAL: Update Tilda's internal hidden fields for city/country
    // These are the fields Tilda uses when it redraws totals
    this.updateTildaHiddenField('tildadelivery-city', city?.name || '');
    this.updateTildaHiddenField('country', city?.country_code?.toLowerCase() || 'ru');
    this.updateTildaHiddenField('postalcode', '');

    // Also update any city input fields in the form
    const cityInputSelectors = [
      'input[name="Город"]',
      'input[name="city"]',
      'input[name="City"]',
      'input[name*="город"]',
      'input[name*="city"]',
    ];
    cityInputSelectors.forEach(selector => {
      const inputs = document.querySelectorAll<HTMLInputElement>(selector);
      inputs.forEach(input => {
        // Only update if it's not our own city input
        if (!input.classList.contains('gw-delivery__city-input')) {
          input.value = city?.name || '';
        }
      });
    });

    console.log('[GW Delivery] Updated Tilda hidden fields:', {
      city: city?.name,
      country: city?.country_code,
      deliveryPrice,
    });

    // Sync delivery data to hidden del_* fields for Tilda → Ткаченко → RetailCRM
    this.syncDeliveryToTildaForm();

    // Update total in Tilda cart
    this.updateTildaTotal();
  }

  /**
   * Update a Tilda hidden field by name
   */
  private updateTildaHiddenField(name: string, value: string): void {
    // Try various selectors that Tilda might use
    const selectors = [
      `input[name="${name}"]`,
      `input[data-tilda-delivery="${name}"]`,
      `input[class*="${name}"]`,
    ];

    for (const selector of selectors) {
      const inputs = document.querySelectorAll<HTMLInputElement>(selector);
      inputs.forEach(input => {
        input.value = value;
      });
    }
  }

  /**
   * Sync delivery data to hidden del_* form fields.
   * These fields are picked up by Tilda form submit → Ткаченко → RetailCRM.
   */
  private syncDeliveryToTildaForm(): void {
    const option = this.state.selectedOption;
    const city = this.state.city;
    if (!option) return;

    const deliveryPrice = option.is_free ? 0 : option.price;

    // Build courier address string
    let courierAddr = '';
    if (option.type === 'courier' && this.state.courierAddress) {
      const a = this.state.courierAddress;
      courierAddr = [a.street, a.house, a.apartment ? `кв ${a.apartment}` : '', a.entrance ? `подъезд ${a.entrance}` : '', a.floor ? `этаж ${a.floor}` : ''].filter(Boolean).join(', ');
    }

    const fields: Record<string, string> = {
      del_provider: option.provider,
      del_type: option.type,
      del_tariff: option.name,
      del_price: String(deliveryPrice),
      del_days: `${option.min_days}-${option.max_days}`,
      del_city: city?.name || '',
      del_pvz_id: this.state.selectedPickupPoint?.id || '',
      del_pvz_name: this.state.selectedPickupPoint?.name || '',
      del_pvz_address: this.state.selectedPickupPoint?.address || '',
      del_address: courierAddr,
    };

    // Find the Tilda form (cart or order form)
    const form = document.querySelector('.t-form') as HTMLFormElement
      || document.querySelector('form[data-formactiontype]') as HTMLFormElement
      || document.querySelector('.t706 form') as HTMLFormElement;

    for (const [name, value] of Object.entries(fields)) {
      // Update all matching inputs across the page
      const existing = document.querySelectorAll<HTMLInputElement>(`input[name="${name}"]`);
      if (existing.length > 0) {
        existing.forEach(input => { input.value = value; });
      } else if (form) {
        // Create hidden input inside the form
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
      }
    }

    console.log('[GW Delivery] Synced del_* fields for RetailCRM:', fields);
  }

  /**
   * Update Tilda cart total - adds delivery row and updates total sum
   */
  updateTildaTotal(): void {
    const option = this.state.selectedOption;
    const city = this.state.city;
    if (!option) return;

    // Prevent recursive calls (Tilda may trigger reDrawTotal when we update DOM)
    const win = window as any;
    if (win.__gwUpdatingTotal) return;
    win.__gwUpdatingTotal = true;

    try {
      const cartData = this.getCartData();
      const deliveryPrice = option.is_free ? 0 : option.price;
      const newTotal = cartData.totalPrice + deliveryPrice;

      // Format price in Russian format
      const formatPrice = (price: number) => `${price.toLocaleString('ru-RU')} ₽`;

      // 1. Update the delivery info display (the "Vienna / Austria" text!)
      this.updateDeliveryInfoDisplay(option, city);

      // 2. Try to add/update delivery price row in cart summary
      this.ensureDeliveryPriceRow(deliveryPrice);

      // 3. Update total value in various Tilda layouts
      // Try multiple approaches to find and update the total
      let totalUpdated = false;

      // Approach A (PRIORITY): Handle Tilda's specific totalamount structure FIRST
      // Structure: .t706__cartwin-totalamount contains:
      //   - .t706__cartwin-prodamount-price (number, may have ₽)
      //   - .t706__cartwin-prodamount-currency (separate ₽ element)
      // We need to update only the price element WITHOUT ₽ if currency element exists
      const totalAmountEl = document.querySelector('.t706__cartwin-totalamount');
      if (totalAmountEl) {
        const priceEl = totalAmountEl.querySelector('.t706__cartwin-prodamount-price');
        const currencyEl = totalAmountEl.querySelector('.t706__cartwin-prodamount-currency');

        if (priceEl) {
          // Format number only (no ₽) if there's a separate currency element
          const numberOnly = newTotal.toLocaleString('ru-RU');
          if (currencyEl) {
            // Separate currency element exists - don't add ₽
            priceEl.textContent = numberOnly;
          } else {
            // No separate currency - add ₽
            priceEl.textContent = formatPrice(newTotal);
          }
          console.log('[GW Delivery] Updated totalamount:', { newTotal, hasCurrency: !!currencyEl });
          totalUpdated = true;
        }
      }

      // Approach B: Look for "Итоговая сумма" text and update sibling (fallback)
      if (!totalUpdated) {
        document.querySelectorAll('*').forEach(el => {
          if (totalUpdated) return;
          const text = el.textContent?.trim() || '';
          if (text === 'Итоговая сумма:' || text === 'Итого:' || text === 'Всего:') {
            // Find the price element - could be next sibling or child of parent
            const parent = el.parentElement;
            if (parent) {
              const priceEl = parent.querySelector('[class*="price"]') ||
                             parent.querySelector('span:last-child') ||
                             el.nextElementSibling;
              if (priceEl && priceEl !== el) {
                // Check for separate currency element
                const currencyEl = parent.querySelector('.t706__cartwin-prodamount-currency');
                const numberOnly = newTotal.toLocaleString('ru-RU');

                if (currencyEl) {
                  // Has separate currency - only set number
                  priceEl.textContent = numberOnly;
                } else {
                  // Check if element has a separate ₽ symbol as child span
                  const rubleSpan = priceEl.querySelector('span');
                  if (rubleSpan && rubleSpan.textContent?.trim() === '₽') {
                    // Find the text node with the number
                    for (const node of priceEl.childNodes) {
                      if (node.nodeType === Node.TEXT_NODE && /\d/.test(node.textContent || '')) {
                        node.textContent = numberOnly + ' ';
                        totalUpdated = true;
                        break;
                      }
                    }
                    if (!totalUpdated) {
                      priceEl.innerHTML = `${numberOnly} <span>₽</span>`;
                      totalUpdated = true;
                    }
                  } else {
                    priceEl.textContent = formatPrice(newTotal);
                    totalUpdated = true;
                  }
                }
                console.log('[GW Delivery] Updated total via text search:', newTotal);
                totalUpdated = true;
              }
            }
          }
        });
      }

      // Approach C: Direct selectors (fallback)
      if (!totalUpdated) {
        const totalSelectors = [
          '.t706__cartwin-prodamount-wrap .t706__cartwin-prodamount-price',
          '.t706__cartwin-bottom .t706__cartwin-prodamount-price',
          '.t706__cartwin-total-value',
          '.t-store__cart__total-price',
          '.t706__form-bottom-text_price',
        ];

        for (const selector of totalSelectors) {
          const totalEls = document.querySelectorAll(selector);
          totalEls.forEach(el => {
            // Check if this looks like a total price (contains number and ₽)
            const text = el.textContent || '';
            if (text.includes('₽') || /\d/.test(text)) {
              // Check if parent has separate currency element
              const parent = el.parentElement;
              const hasCurrency = parent?.querySelector('.t706__cartwin-prodamount-currency');
              if (hasCurrency) {
                el.textContent = newTotal.toLocaleString('ru-RU');
              } else {
                el.textContent = formatPrice(newTotal);
              }
              console.log('[GW Delivery] Updated total via selector:', { selector, newTotal });
              totalUpdated = true;
            }
          });
          if (totalUpdated) break;
        }
      }

      // 4. CRITICAL: Update Tilda's internal totals
      const tcart = (window as any).tcart;
      if (tcart) {
        // Store original product total for reference
        tcart.__gwProductsTotal = cartData.totalPrice;
        tcart.__gwDeliveryPrice = deliveryPrice;

        // Update tcart.amount to include delivery!
        // This is what Tilda uses for final checkout
        const prodAmount = parseFloat(tcart.prodamount) || cartData.totalPrice;
        tcart.amount = prodAmount + deliveryPrice;

        console.log('[GW Delivery] Updated tcart.amount:', {
          prodamount: tcart.prodamount,
          deliveryPrice,
          amount: tcart.amount,
        });
      }
    } finally {
      // Release the lock after a short delay to allow DOM to settle
      setTimeout(() => {
        win.__gwUpdatingTotal = false;
      }, 50);
    }
  }

  /**
   * Update the delivery info display text (the "Vienna / Austria" area)
   * This is a separate element from our widget that Tilda uses to show selected delivery
   */
  private updateDeliveryInfoDisplay(option: DeliveryOption, city: City | null): void {
    const cityName = city?.name || '';
    const countryName = city?.country_code === 'RU' ? 'Россия' :
                       city?.country_code === 'BY' ? 'Беларусь' :
                       city?.country_code === 'KZ' ? 'Казахстан' : '';

    // Format: "Москва / Россия" or just "Москва"
    const locationText = countryName ? `${cityName} / ${countryName}` : cityName;

    // Use TreeWalker to find ALL text nodes containing Vienna/Austria
    const walker = document.createTreeWalker(
      document.body,
      NodeFilter.SHOW_TEXT,
      null
    );

    const nodesToUpdate: Text[] = [];
    let node: Text | null;
    while (node = walker.nextNode() as Text) {
      const text = node.textContent || '';
      if (text.includes('Vienna') || text.includes('Austria')) {
        nodesToUpdate.push(node);
      }
    }

    // Replace text in all found nodes
    let replacedCount = 0;
    nodesToUpdate.forEach(textNode => {
      if (textNode.parentElement) {
        let newText = textNode.textContent || '';
        // Replace patterns like "Vienna / Austria" or just "Vienna"
        newText = newText.replace(/Vienna\s*\/?\s*Austria/gi, locationText);
        newText = newText.replace(/Vienna/gi, cityName);
        newText = newText.replace(/Austria/gi, countryName || '');

        if (newText !== textNode.textContent) {
          textNode.textContent = newText;
          replacedCount++;
        }
      }
    });

    if (replacedCount > 0) {
      console.log('[GW Delivery] Replaced Vienna text in', replacedCount, 'nodes with:', locationText);
    }
  }

  /**
   * Show skeleton loading state for price breakdown
   */
  showPriceBreakdownSkeleton(): void {
    const totalsContainer = document.querySelector('.t706__cartpage-totals');
    if (!totalsContainer) return;

    // Hide ALL original Tilda price rows to prevent duplication
    const rowsToHide = [
      '.t706__cartpage-prodamount',
      '.t706__cartwin-prodamount-wrap',
      '.t706__cartpage-delivery',
      '.t706__cartwin-delivery',
    ];

    rowsToHide.forEach(selector => {
      const row = totalsContainer.querySelector(selector);
      if (row) {
        (row as HTMLElement).style.display = 'none';
      }
    });

    // Hide any Tilda-generated "Сумма:" or delivery rows
    totalsContainer.querySelectorAll('*').forEach(el => {
      const text = el.textContent?.trim() || '';
      if ((text.startsWith('Сумма:') || text.includes('СДЭК') || text.includes('Курьер'))
          && !el.closest('#gw-price-breakdown')
          && !el.closest('.t706__cartwin-totalamount-wrap')) {
        const isDirectContainer = el.childNodes.length <= 3;
        if (isDirectContainer && el.tagName !== 'BODY' && el.tagName !== 'HTML') {
          (el as HTMLElement).style.display = 'none';
        }
      }
    });

    // Create or get breakdown container
    let breakdownContainer = document.getElementById('gw-price-breakdown');
    if (!breakdownContainer) {
      breakdownContainer = document.createElement('div');
      breakdownContainer.id = 'gw-price-breakdown';
      breakdownContainer.style.cssText = `
        font-size: 14px;
        line-height: 1.6;
        color: #666;
        margin-bottom: 12px;
      `;

      const totalRow = totalsContainer.querySelector('.t706__cartwin-totalamount-wrap');
      if (totalRow) {
        totalRow.insertAdjacentElement('beforebegin', breakdownContainer);
      } else {
        totalsContainer.appendChild(breakdownContainer);
      }
    }

    // Show skeleton rows
    breakdownContainer.innerHTML = `
      <div style="display: flex; justify-content: space-between; padding: 4px 0;">
        <span>Товары:</span>
        <span class="gw-price-skeleton" style="width: 70px;"></span>
      </div>
      <div style="display: flex; justify-content: space-between; padding: 4px 0;">
        <span>Доставка:</span>
        <span class="gw-price-skeleton" style="width: 80px;"></span>
      </div>
    `;

    // Also add skeleton to total
    const totalAmountEl = document.querySelector('.t706__cartwin-totalamount');
    if (totalAmountEl) {
      totalAmountEl.classList.add('gw-price-fade', 'gw-price-fade-out');
    }
  }

  /**
   * Ensure price breakdown exists in cart summary
   * Shows: Товары, Скидка (if any), Доставка, Итого
   */
  private ensureDeliveryPriceRow(deliveryPrice: number): void {
    // Find the totals container
    const totalsContainer = document.querySelector('.t706__cartpage-totals');
    if (!totalsContainer) {
      console.log('[GW Delivery] Totals container not found');
      return;
    }

    // Get cart data for breakdown
    const cartData = this.getCartData();
    const productsTotal = cartData.totalPrice;

    // Check for discounts in tcart
    const tcart = (window as any).tcart;
    const discount = tcart?.discount ? parseFloat(tcart.discount) : 0;

    // Calculate final total
    const finalTotal = productsTotal - discount + deliveryPrice;

    // Hide ALL original Tilda price rows - we'll replace them with our breakdown
    // This prevents duplication of "Сумма:", delivery rows, etc.
    const rowsToHide = [
      '.t706__cartpage-prodamount',           // Original sum row
      '.t706__cartwin-prodamount-wrap',       // Another sum wrapper
      '.t706__cartpage-delivery',             // Tilda's delivery row
      '.t706__cartwin-delivery',              // Another delivery row format
    ];

    rowsToHide.forEach(selector => {
      const row = totalsContainer.querySelector(selector);
      if (row) {
        (row as HTMLElement).style.display = 'none';
      }
    });

    // Also hide any rows containing "Сумма:" or delivery text that Tilda might add
    totalsContainer.querySelectorAll('*').forEach(el => {
      const text = el.textContent?.trim() || '';
      // Hide elements that contain "Сумма:" but are NOT our breakdown
      if ((text.startsWith('Сумма:') || text.includes('СДЭК') || text.includes('Курьер'))
          && !el.closest('#gw-price-breakdown')
          && !el.closest('.t706__cartwin-totalamount-wrap')) {
        // Check if this is a direct container for these texts (not a parent that contains our breakdown too)
        const isDirectContainer = el.childNodes.length <= 3; // Simple heuristic
        if (isDirectContainer && el.tagName !== 'BODY' && el.tagName !== 'HTML') {
          (el as HTMLElement).style.display = 'none';
        }
      }
    });

    // Check if our breakdown container already exists
    let breakdownContainer = document.getElementById('gw-price-breakdown');

    if (!breakdownContainer) {
      // Create breakdown container
      breakdownContainer = document.createElement('div');
      breakdownContainer.id = 'gw-price-breakdown';
      breakdownContainer.style.cssText = `
        font-size: 14px;
        line-height: 1.6;
        color: #666;
        margin-bottom: 12px;
      `;

      // Insert before the total row
      const totalRow = totalsContainer.querySelector('.t706__cartwin-totalamount-wrap');
      if (totalRow) {
        totalRow.insertAdjacentElement('beforebegin', breakdownContainer);
      } else {
        totalsContainer.appendChild(breakdownContainer);
      }
    }

    // Build breakdown rows with fade-in animation
    const rows: string[] = [];

    // Row 1: Товары (products total)
    rows.push(`
      <div style="display: flex; justify-content: space-between; padding: 4px 0;">
        <span>Товары:</span>
        <span style="color: #333;">${productsTotal.toLocaleString('ru-RU')} ₽</span>
      </div>
    `);

    // Row 2: Скидка (if any)
    if (discount > 0) {
      rows.push(`
        <div style="display: flex; justify-content: space-between; padding: 4px 0;">
          <span>Скидка:</span>
          <span style="color: #e74c3c;">-${discount.toLocaleString('ru-RU')} ₽</span>
        </div>
      `);
    }

    // Row 3: Доставка
    const deliveryText = deliveryPrice === 0
      ? '<span style="color: #27ae60;">Бесплатно</span>'
      : `<span style="color: #333;">+${deliveryPrice.toLocaleString('ru-RU')} ₽</span>`;

    const optionName = this.state.selectedOption?.name || 'Доставка';
    rows.push(`
      <div style="display: flex; justify-content: space-between; padding: 4px 0;">
        <span>${optionName}:</span>
        ${deliveryText}
      </div>
    `);

    breakdownContainer.innerHTML = rows.join('');

    console.log('[GW Delivery] Price breakdown updated:', {
      productsTotal,
      discount,
      deliveryPrice,
      finalTotal
    });
  }

  /**
   * Get cart data from Tilda
   * CRITICAL: Always read quantity from DOM inputs - tcart may not be updated yet!
   */
  private getCartData(): CartData {
    const MIN_DIM = 10; // CDEK minimum

    // FIRST: Build a map of quantities from DOM inputs (MOST UP-TO-DATE SOURCE!)
    // This is critical because Tilda's tcart.products[].quantity is NOT updated
    // immediately when user manually types a new quantity
    const domQuantities: Record<string, number> = {};
    document.querySelectorAll('.t706__cartwin-products-i, .t706__product, .t-store__cart__item').forEach((el) => {
      const sku = el.getAttribute('data-product-sku') || '';
      // Tilda uses SPAN for quantity display, not input!
      // First try span, then input as fallback
      const qtySpan = el.querySelector<HTMLElement>('.t706__product-quantity');
      const qtyInput = el.querySelector<HTMLInputElement>('.t706__product-quantity-inp, .t706__cartwin-prodamount-input');

      let qty = 1;
      if (qtySpan) {
        qty = parseInt(qtySpan.textContent || '1', 10) || 1;
      } else if (qtyInput) {
        qty = parseInt(qtyInput.value || '1', 10) || 1;
      }

      if (sku) {
        domQuantities[sku] = qty;
      }
    });

    console.log('[GW Delivery] DOM quantities:', domQuantities);

    // Try Tilda API for product info (weight, dimensions, price)
    // BUT use DOM quantities instead of tcart.products[].quantity!
    const tildaCart = (window as any).tcart;
    if (tildaCart?.products) {
      const items = tildaCart.products.map((p: any) => {
        const sku = p.sku || '';
        // USE DOM QUANTITY - it's the most up-to-date!
        const quantity = domQuantities[sku] !== undefined
          ? domQuantities[sku]
          : (parseInt(p.quantity, 10) || 1);

        return {
          sku,
          quantity,
          price: parseFloat(p.price) || 0,
          weight: parseFloat(p.pack_m || p.weight || 0), // already in grams!
          // Dimensions in Tilda are in mm, convert to cm
          length: Math.ceil(parseFloat(p.pack_x || '0') / 10) || MIN_DIM,
          width: Math.ceil(parseFloat(p.pack_y || '0') / 10) || MIN_DIM,
          height: Math.ceil(parseFloat(p.pack_z || '0') / 10) || MIN_DIM,
        };
      });

      const totalPrice = items.reduce((sum: number, item: any) => sum + item.price * item.quantity, 0);
      const totalWeight = items.reduce((sum: number, item: any) => sum + item.weight * item.quantity, 0);

      // Calculate aggregate dimensions
      const maxLength = Math.max(...items.map((i: any) => i.length), MIN_DIM);
      const maxWidth = Math.max(...items.map((i: any) => i.width), MIN_DIM);
      const totalHeight = items.reduce((sum: number, i: any) => sum + i.height * i.quantity, 0);

      console.log('[GW Delivery] Cart data from tcart + DOM:', { totalPrice, items: items.map((i: any) => ({ sku: i.sku, qty: i.quantity, price: i.price })) });

      return {
        items,
        totalPrice,
        totalWeight: totalWeight || 500,
        dimensions: {
          length: Math.min(maxLength, 120),
          width: Math.min(maxWidth, 80),
          height: Math.min(Math.max(totalHeight, MIN_DIM), 80),
        },
      };
    }

    // Fallback: parse everything from DOM
    const items: any[] = [];
    document.querySelectorAll('.t706__cartwin-products-i, .t706__product, .t-store__cart__item').forEach((el) => {
      const sku = el.getAttribute('data-product-sku') || '';
      // Tilda uses SPAN for quantity, not input!
      const qtySpan = el.querySelector<HTMLElement>('.t706__product-quantity');
      const qtyInput = el.querySelector<HTMLInputElement>('.t706__product-quantity-inp, .t706__cartwin-prodamount-input');
      const quantity = qtySpan
        ? parseInt(qtySpan.textContent || '1', 10)
        : parseInt(qtyInput?.value || '1', 10);
      const priceEl = el.querySelector('.t706__cartwin-prodprice, .t706__product-amount, .t-store__cart__item-price');
      const price = parseFloat(priceEl?.textContent?.replace(/[^\d.]/g, '') || '0');
      const weight = parseFloat(el.getAttribute('data-product-weight') || '0'); // grams

      items.push({ sku, quantity, price, weight: weight || 100 });
    });

    const totalPrice = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const totalWeight = items.reduce((sum, item) => sum + item.weight * item.quantity, 0);

    console.log('[GW Delivery] Cart data from DOM fallback:', { totalPrice, items });

    return {
      items,
      totalPrice: totalPrice || 1000,
      totalWeight: totalWeight || 500,
      dimensions: { length: 20, width: 15, height: 15 }, // defaults
    };
  }

  /**
   * Refresh calculation (call when cart changes)
   */
  async refresh(): Promise<void> {
    const cartData = this.getCartData();
    await this.calculateDelivery(cartData);
  }

  /**
   * Collect full order payload for submission to backend
   * Called at checkout form submit time
   */
  collectOrderPayload(): DeliveryOrderPayload | null {
    if (!this.state.selectedOption || !this.state.city) {
      console.warn('[GW Delivery] Cannot collect order payload: no option or city selected');
      return null;
    }

    const cartData = this.getCartData();
    const customer = this.getCustomerData();
    const option = this.state.selectedOption;
    const city = this.state.city;

    const payload: DeliveryOrderPayload = {
      cartTotal: cartData.totalPrice,
      cartItems: cartData.items,
      city: {
        id: city.id,
        name: city.name,
        cdekId: city.cdek_city_id,
      },
      selectedOption: {
        provider: option.provider,
        tariffCode: option.id,
        tariffName: option.name,
        type: option.type,
        price: option.is_free ? 0 : option.price,
        deliveryDays: {
          min: option.min_days,
          max: option.max_days,
        },
      },
      timestamp: new Date().toISOString(),
    };

    // Add pickup point data if selected
    if (this.state.selectedPickupPoint) {
      payload.pickupPoint = {
        id: this.state.selectedPickupPoint.id,
        name: this.state.selectedPickupPoint.name,
        address: this.state.selectedPickupPoint.address,
        provider: this.state.selectedPickupPoint.provider,
      };
    }

    // Add courier address if courier delivery
    if (option.type === 'courier' && this.state.courierAddress) {
      const addr = this.state.courierAddress;
      const addressParts = [addr.street, addr.house, addr.apartment].filter(Boolean);
      payload.courierAddress = {
        street: addr.street || '',
        house: addr.house || '',
        apartment: addr.apartment,
        floor: addr.floor,
        comment: addr.comment,
        fullAddress: addressParts.join(', '),
      };
    }

    // Add customer data from Tilda form
    if (customer) {
      payload.customer = customer;
    }

    // Try to get Tilda order ID
    const tcart = (window as any).tcart;
    if (tcart?.orderId) {
      payload.orderId = String(tcart.orderId);
    }

    console.log('[GW Delivery] Collected order payload:', payload);
    return payload;
  }

  /**
   * Extract customer data from Tilda form fields
   */
  getCustomerData(): { name?: string; phone?: string; email?: string } | null {
    const result: { name?: string; phone?: string; email?: string } = {};
    let found = false;

    // Name selectors
    const nameSelectors = [
      'input[name="Name"]', 'input[name="name"]',
      '.t-input-group_nm input',
    ];
    for (const sel of nameSelectors) {
      const input = document.querySelector<HTMLInputElement>(sel);
      if (input && input.value.trim()) {
        result.name = input.value.trim();
        found = true;
        break;
      }
    }

    // Phone selectors
    const phoneSelectors = [
      'input[name="Phone"]', 'input[name="phone"]',
      '.t-input-group_ph input[type="tel"]',
      '.t-input-group_ph input',
    ];
    for (const sel of phoneSelectors) {
      const input = document.querySelector<HTMLInputElement>(sel);
      if (input && input.value.trim()) {
        result.phone = input.value.trim();
        found = true;
        break;
      }
    }

    // Email selectors
    const emailSelectors = [
      'input[name="Email"]', 'input[name="email"]',
      'input[type="email"]',
      '.t-input-group_em input',
    ];
    for (const sel of emailSelectors) {
      const input = document.querySelector<HTMLInputElement>(sel);
      if (input && input.value.trim()) {
        result.email = input.value.trim();
        found = true;
        break;
      }
    }

    return found ? result : null;
  }

  /**
   * Save current delivery selection to localStorage buffer
   */
  saveToBuffer(): void {
    const payload = this.collectOrderPayload();
    if (payload) {
      try {
        localStorage.setItem(LS_ORDER_BUFFER_KEY, JSON.stringify(payload));
      } catch (e) {
        console.warn('[GW Delivery] Failed to save order buffer:', e);
      }
    }
  }

  /**
   * Get saved order buffer from localStorage
   */
  static getBufferedPayload(): DeliveryOrderPayload | null {
    try {
      const raw = localStorage.getItem(LS_ORDER_BUFFER_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as DeliveryOrderPayload;
    } catch {
      return null;
    }
  }

  /**
   * Clear the order buffer from localStorage
   */
  static clearBuffer(): void {
    try {
      localStorage.removeItem(LS_ORDER_BUFFER_KEY);
    } catch (e) { /* ignore */ }
  }
}

export default DeliveryWidget;

